import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useSearchParams,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as ProductHeart } from "./../assets/svgs/product-heart.svg";
import VirtualTryOnFlowModal from "../components/VirtualTryOnFlowModal.js";
import { ReactComponent as SurveyLockedIcon } from "./../assets/svgs/survey-locked.svg";
import { ReactComponent as CartIcon } from "./../assets/svgs/cart-icon.svg";
import { ReactComponent as AskEvenessIcon } from "./../assets/svgs/ask-eveness-icon.svg";
import { ReactComponent as ItemNotFoundIcon } from "./../assets/svgs/item-not-found.svg";
import FloatingChatBar from "../components/FloatingChatBar.js";
import AddedToCartDrawer from "../components/AddedToCartDrawer.js";
import { useMarketplaceChat } from "../context/MarketplaceChatProvider.js";
import ProfileMeasurementsForm from "../components/ProfileMeasurementsForm.js";
import HomeProfilePopupFlow from "../components/HomeProfilePopupFlow.js";
import ExclusiveBundleSizingModal from "../components/ExclusiveBundleSizingModal.js";
import { useExclusiveBundleFlow } from "../hooks/useExclusiveBundleFlow.js";
import Footer from "../components/Footer.js";
import toast from "react-hot-toast";
import { Helmet } from "@dr.pogodin/react-helmet";
import productSeoMap from "../generated/productSeoMap.json";
import {
  useMarketplaceProductDetailsCachedQuery,
  useExclusiveTopProductsCachedQuery,
  usePublicTopProductsCachedQuery,
  useUserSingleMarketplaceProductStreamCachedQuery,
  useUserTopProductsCachedQuery,
  usePublicCollectionTopProductsCachedQuery,
  useUserCollectionTopProductsCachedQuery,
  useProfileReadQuery,
  useGetUserFullBodyImageQuery,
  usePostUploadImageMutation,
  useLazyMultiGarmentTryOnStreamQuery,
} from "../store/api.ts";
import SaveToBoardModal from "../components/SaveToBoardModal.js";
import {
  uploadToS3,
  getVisibleCardMatchPercentage,
  formatProductPrice,
  formatStorePrice,
  getProductPricing,
  PENDING_SURVEY_TOKEN_KEY,
} from "../utils.js";
import StableMasonryGrid from "../components/StableMasonryGrid.js";
import { addToCart, selectIsInCart, selectStandaloneCartItems } from "../store/cartSlice.js";
import { logAddToCart } from "../utils/analytics";
import QuickPayButton from "../components/QuickPayButton.js";
import MarkdownResponse from "../components/MarkdownResponse";
import NewNavbar from "../components/NewNavbar.js";
import ProductCard from "../components/ProductCard.js";
import AuthModal from "../components/AuthModal.js";
import RootPersonalizationModal from "../components/RootPersonalizedModal.js";

const sizeOptions = ["XS", "S", "M", "L", "XL", "XXL"];
const TOP_PRODUCTS_PAGE_SIZE = 20;
const getWebsiteDomain = (url) => {
  if (!url) return "website.com";

  try {
    const hostname = new URL(url).hostname;

    return hostname.replace(/^www\./, "");
  } catch {
    return "website.com";
  }
};
const isEvenessUrl = (url) => {
  if (!url) return false;

  try {
    const hostname = new URL(url).hostname.replace(/^www\./, "");
    return hostname === "eveness.ai";
  } catch {
    return false;
  }
};

const GUEST_PUBLIC_MATCH_PRODUCT_PREFIX = "guest_public_match_product";

const getGuestPublicMatchProductKey = (token) => {
  if (!token) return "";
  return `${GUEST_PUBLIC_MATCH_PRODUCT_PREFIX}:${token}`;
};

const getGuestPublicMatchProduct = (token) => {
  try {
    return localStorage.getItem(getGuestPublicMatchProductKey(token)) || "";
  } catch {
    return "";
  }
};

const markGuestPublicMatchProduct = ({ token, productKey }) => {
  try {
    localStorage.setItem(getGuestPublicMatchProductKey(token), productKey);
  } catch {}
};

const EXCLUSIVE_ROUTE_SLUG_SUFFIX = "-amir-col";

const isMarketplaceSlug = (slug) => {
  return String(slug || "")
    .trim()
    .startsWith("mrk-");
};

const isExclusiveSlug = (slug) => {
  return String(slug || "")
    .trim()
    .toLowerCase()
    .endsWith(EXCLUSIVE_ROUTE_SLUG_SUFFIX);
};

const normalizeStockQuantity = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const parsedQuantity = Number(value);

  if (!Number.isFinite(parsedQuantity)) {
    return null;
  }

  return Math.max(0, parsedQuantity);
};

const normalizeCollectionVariants = (variants = []) => {
  if (!Array.isArray(variants)) return [];

  return variants
    .map((variant) => {
      const pricing = getProductPricing(variant, true);
      const quantity = normalizeStockQuantity(
        variant?.quantity,
      );

      return {
        id: Number(variant?.id),
        size: String(variant?.size || "")
          .trim()
          .toUpperCase(),
        color: variant?.color || "",
        price: formatProductPrice(variant?.price),
        original_price: variant?.price ?? "",
        originalPrice: pricing.originalPrice,
        discounted_price:
          variant?.discounted_price ??
          variant?.price ??
          "",
        discountedPrice: pricing.discountedPrice,
        discount:
          variant?.discount ??
          variant?.discount_percent ??
          0,
        discountPercent: pricing.discountPercent,
        savings: pricing.savings,
        hasDiscount: pricing.hasDiscount,
        quantity,
        isOutOfStock:
          quantity !== null && quantity <= 0,
        image:
          variant?.image &&
          !String(variant.image).endsWith("/null") &&
          !String(variant.image).includes("null")
            ? variant.image
            : "",
      };
    })
    .filter((variant) => variant.id && variant.size);
};

const normalizeProductDetails = (response, productType = "marketplace") => {
  if (!response) return null;

  const responseSlug =
    response?.slug ??
    response?.product_url ??
    "";

  const isInternalVariantProduct =
    (productType === "product" || productType === "exclusive") &&
    !isMarketplaceSlug(responseSlug);

  const pricing = getProductPricing(
    response,
    isInternalVariantProduct,
  );

  const quantity = normalizeStockQuantity(
    response?.quantity,
  );

  const images = [
    response?.image_url || response?.image,
    response?.image_second,
    response?.image_third,
    response?.image_forth,
  ].filter((image) => {
    if (!image) return false;

    const imageValue = String(image);

    return (
      imageValue !== "null" &&
      imageValue !== "undefined" &&
      !imageValue.endsWith("/null") &&
      !imageValue.includes("/null")
    );
  });

  const variants = isInternalVariantProduct
    ? normalizeCollectionVariants(response?.variants)
    : [];

  const rawUrl = response?.product_url ?? response?.slug ?? "";

  const publicSlug =
    response?.slug ||
    (isInternalVariantProduct ? rawUrl : "");

  return {
    id: Number(response?.id),
    name: response?.product_name ?? response?.name ?? "",
    url: isInternalVariantProduct ? "" : rawUrl,
    slug: publicSlug,
    real_slug:
      response?.slug ||
      (isInternalVariantProduct ? rawUrl : ""),
    image: images[0] || "",
    image_url: images[0] || "",
    images,
    main_images: images,
    price: formatProductPrice(response?.price),
    original_price: response?.price ?? "",
    originalPrice: pricing.originalPrice,
    discounted_price:
      response?.discounted_price ??
      response?.price ??
      "",
    discountedPrice:
      pricing.discountedPrice,
    discount:
      response?.discount ??
      response?.discount_percent ??
      0,
    discountPercent:
      pricing.discountPercent,
    savings:
      pricing.savings,
    hasDiscount:
      pricing.hasDiscount,
    garmentType: response?.garment_type ?? response?.category ?? "",
    description:
      response?.product_generated_description ?? response?.description ?? "",
    metaTitle: response?.meta_title ?? null,
    metaDescription: response?.meta_description ?? null,
    keywords: Array.isArray(response?.keywords) ? response.keywords : [],      
    brand:
      response?.brand ??
      response?.distributor ??
      "",
    color:
      response?.color ??
      variants[0]?.color ??
      "",
    is_marketplace: productType === "marketplace",
    productType,
    product_type: productType,
    marketplace_url:
      productType === "marketplace"
        ? rawUrl
        : "",
    quantity,
      isOutOfStock: quantity !== null && quantity <= 0,    
    variants,
  };
};

const getLocalPendingSurveyToken = () => {
  if (typeof window === "undefined") return "";

  try {
    return window.localStorage.getItem(PENDING_SURVEY_TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

const clearSessionTokenIfLocalTokenMissing = () => {
  if (typeof window === "undefined") return;

  try {
    const localToken = window.localStorage.getItem(PENDING_SURVEY_TOKEN_KEY);

    if (!localToken) {
      window.sessionStorage.removeItem(PENDING_SURVEY_TOKEN_KEY);
    }
  } catch {}
};

const normalizeTopProducts = (response, productType = "marketplace") => {
  const products = Array.isArray(response?.similar_products)
    ? response.similar_products
    : Array.isArray(response)
      ? response
      : [];

  return products.map((item) => {
    const score =
      item?.match_percentage ??
      item?.march_percentage ??
      item?.product_score ??
      null;

    const rawItemSlug =
      item?.real_slug ??
      item?.slug ??
      item?.product_url ??
      item?.url ??
      "";

    const isInternalVariantItem =
      productType === "product" ||
      productType === "exclusive";

    const pricing = getProductPricing(
      item,
      isInternalVariantItem,
    );

    const publicSlug = rawItemSlug;
    return {
      id: Number(item?.id),
      name: item?.product_name ?? item?.name ?? "",
      url:
        productType === "marketplace"
          ? (item?.product_url ?? item?.url ?? "")
          : publicSlug,
      slug: publicSlug,
      real_slug:
        item?.real_slug ??
        item?.slug ??
        "",
      image:
        item?.image_url ??
        item?.image ??
        "",
      price: formatProductPrice(item?.price),
      original_price: item?.price ?? "",
      originalPrice: pricing.originalPrice,
      discounted_price:
        item?.discounted_price ?? "",
      discountedPrice: pricing.discountedPrice,
      discount:
        item?.discount ??
        item?.discount_percent ??
        0,
      discountPercent: pricing.discountPercent,
      hasDiscount: pricing.hasDiscount,
      garmentType: item?.garment_type ?? item?.category ?? "",
      brand: item?.brand ?? item?.distributor ?? "",
      matchPercentage:
        score !== null && score !== undefined
          ? Math.round(Number(score))
          : null,
      productType,
      product_type: productType,
      is_marketplace: productType === "marketplace",
    };
  });
};

const normalizeMatchScore = (response) => {
  if (!response) return null;

  const rawRecommendedSize =
    response?.recommended_size ??
    response?.recommendedSize ??
    null;

  const rawPercentage =
    response?.match_percentage ??
    response?.matchPercentage ??
    response?.match_score ??
    response?.matchScore ??
    response?.product_score ??
    response?.score ??
    null;

  const rawSummary =
    response?.match_summary ??
    response?.matchSummary ??
    response?.match_description ??
    response?.description ??
    response?.summary ??
    response?.message ??
    "";

  return {
    recommendedSize:
      rawRecommendedSize !== null &&
      rawRecommendedSize !== undefined &&
      rawRecommendedSize !== ""
        ? String(rawRecommendedSize).trim().toUpperCase()
        : null,
    matchPercentage:
      rawPercentage !== null &&
      rawPercentage !== undefined &&
      rawPercentage !== ""
        ? Math.round(Number(rawPercentage))
        : null,
    matchSummary: typeof rawSummary === "string" ? rawSummary : "",
    tags: Array.isArray(response?.tags) ? response.tags : [],
  };
};

const emptyMatchData = {
  recommendedSize: null,
  matchPercentage: null,
  matchSummary: "",
  tags: [],
};

const imageHeightClasses = [
  "h-[220px] sm:h-[310px]",
  "h-[240px] sm:h-[340px]",
  "h-[260px] sm:h-[370px]",
  "h-[280px] sm:h-[400px]",
];

const getProductImageHeightClass = (item, index) => {
  const source = String(item?.id ?? item?.name ?? index);
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }
  const safeIndex = Math.abs(hash) % imageHeightClasses.length;
  return imageHeightClasses[safeIndex];
};

function FashionDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    closeChatModal,
    startProductConversation,
    isProductTransferLoading,
    isStreaming: isGlobalChatStreaming,
  } = useMarketplaceChat();
  const [searchParams, setSearchParams] = useSearchParams();
  const { slug: slugFromRoute } = useParams();
  const [detailSurveyProfile, setDetailSurveyProfile] = useState(null);
  const [ isCartDrawerOpen, setIsCartDrawerOpen ] = useState(false);
  const [ cartDrawerProductId, setCartDrawerProductId] = useState(null);
  const [cartDrawerBundleId, setCartDrawerBundleId] = useState(null);
  const [cartDrawerBundleInstanceId, setCartDrawerBundleInstanceId] = useState(null);

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [failedProductImages, setFailedProductImages] = useState(new Set());
  const handleProductImageError = (imageUrl) => {
    if (!imageUrl) return;
    setFailedProductImages((prev) => {
      const next = new Set(prev);
      next.add(imageUrl);
      return next;
    });
  };
  const [homeProfileInitialStep, setHomeProfileInitialStep] = useState(0);
  const [isFitScoreAccountFlow, setIsFitScoreAccountFlow] = useState(false);
  const [authTick, setAuthTick] = useState(0);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("login");
  const [autoStartGoogle, setAutoStartGoogle] = useState(false);
  const [isHomeProfileFlowOpen, setIsHomeProfileFlowOpen] = useState(false);
  const [rootPersonalizationOpen, setRootPersonalizationOpen] = useState(false);
  const [isTryOnModalOpen, setIsTryOnModalOpen] = useState(false);
  const [uploadedTryOnFile, setUploadedTryOnFile] = useState(null);
  const [uploadedTryOnPreview, setUploadedTryOnPreview] = useState("");
  const [tryOnImage, setTryOnImage] = useState("");
  const [selectedTryOnSource, setSelectedTryOnSource] = useState("");
  const [isPreparingMultiTryOn, setIsPreparingMultiTryOn] = useState(false);
  const completedMultiTryOnRequestRef = useRef("");

  const authToken = useMemo(() => {
    return localStorage.getItem("authToken");
  }, [authTick]);

  const isAuthenticated = Boolean(authToken);
  const [pendingSurveyToken, setPendingSurveyToken] = useState(() => {
    clearSessionTokenIfLocalTokenMissing();
    return getLocalPendingSurveyToken();
  });  
  
  const hasPendingSurveyToken = Boolean(pendingSurveyToken);

  const syncPendingSurveyToken = useCallback(() => {
    clearSessionTokenIfLocalTokenMissing();
    setPendingSurveyToken(getLocalPendingSurveyToken());
  }, []);

  useEffect(() => {
    syncPendingSurveyToken();

    window.addEventListener("storage", syncPendingSurveyToken);
    window.addEventListener("focus", syncPendingSurveyToken);
    window.addEventListener("eveness-auth-changed", syncPendingSurveyToken);
    window.addEventListener("eveness-survey-token-changed", syncPendingSurveyToken);

    return () => {
      window.removeEventListener("storage", syncPendingSurveyToken);
      window.removeEventListener("focus", syncPendingSurveyToken);
      window.removeEventListener("eveness-auth-changed", syncPendingSurveyToken);
      window.removeEventListener("eveness-survey-token-changed", syncPendingSurveyToken);
    };
  }, [syncPendingSurveyToken]);

  useEffect(() => {
    syncPendingSurveyToken();
  }, [location.pathname, location.search, syncPendingSurveyToken]);

  const openLoginModal = () => {
    setAutoStartGoogle(false);
    setAuthModalMode("login");
    setAuthModalOpen(true);
  };

  const openFitScoreAccountFlow = () => {
    if (isAuthenticated) {
      openLoginModal();
      return;
    }
    setAuthModalOpen(false);
    setRootPersonalizationOpen(false);
    setHomeProfileInitialStep(5);
    setIsFitScoreAccountFlow(true);
    setIsHomeProfileFlowOpen(true);
  };

  const handleCloseHomeProfileFlow = () => {
    setIsHomeProfileFlowOpen(false);
    setHomeProfileInitialStep(0);
    setIsFitScoreAccountFlow(false);
    activateGuestMatchForCurrentProduct();
  };

  const handleHomeProfileSignIn = () => {
    activateGuestMatchForCurrentProduct();
    setIsHomeProfileFlowOpen(false);
    setHomeProfileInitialStep(0);
    setIsFitScoreAccountFlow(false);
    setAutoStartGoogle(false);
    setAuthModalMode("login");
    setAuthModalOpen(true);
  };

  const handleHomeProfileSignUp = ({ provider, profile }) => {
    console.log("Home profile data:", profile);
    activateGuestMatchForCurrentProduct();
    setIsHomeProfileFlowOpen(false);
    setHomeProfileInitialStep(0);
    setIsFitScoreAccountFlow(false);

    if (provider === "google") {
      setAuthModalMode("signup");
      setAutoStartGoogle(true);
      setAuthModalOpen(true);
      return;
    }

    setAutoStartGoogle(false);
    setAuthModalMode("signup");
    setAuthModalOpen(true);
  };

  useEffect(() => {
    if (isAuthenticated) {
      setIsHomeProfileFlowOpen(false);
      setHomeProfileInitialStep(0);
      setIsFitScoreAccountFlow(false);
    }
  }, [isAuthenticated]);

  const {
    data: profileData,
    isSuccess: isProfileSuccess,
    isError: isProfileError,
    error: profileError,
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
    refetch: refetchProfile,
  } = useProfileReadQuery(undefined, {
    skip: !isAuthenticated,
  });

  const {
    data: userFullBodyImageData,
    isFetching: isSurveyImageFetching,
    refetch: refetchSurveyImage,
  } = useGetUserFullBodyImageQuery(undefined, {
    skip: !isAuthenticated,
  });

  const [postUploadImage, { isLoading: isUploadingTryOnImage }] = usePostUploadImageMutation();

  const [
    triggerMultiGarmentTryOn,
    {
      data: multiTryOnStream,
      isFetching: isMultiTryOnStarting,
      reset: resetMultiTryOnStream,
    },
  ] = useLazyMultiGarmentTryOnStreamQuery();

  const isMultiTryOnStreaming = Boolean(multiTryOnStream && !multiTryOnStream.done && !multiTryOnStream.error);
  const isMultiTryOnBusy = isPreparingMultiTryOn || isMultiTryOnStarting || isMultiTryOnStreaming;
  const hasCompletedSurvey = isAuthenticated && isProfileSuccess && Boolean(profileData);
  const shouldUseSignedMarketplaceApis = hasCompletedSurvey;

  const profileMissing =
    isAuthenticated &&
    isProfileError &&
    (
      profileError?.status === 404 ||
      profileError?.originalStatus === 404
    );
  const shouldShowSurveyPrompt =
    !hasPendingSurveyToken &&
    (
      !isAuthenticated ||
      profileMissing
    );

  useEffect(() => {
    if (hasPendingSurveyToken) {
      setRootPersonalizationOpen(false);
    }
  }, [hasPendingSurveyToken]);    

  const canUseRealChat = hasCompletedSurvey;

  const isProfileChecking =
    isAuthenticated && (isProfileLoading || isProfileFetching);

  const productIdFromState = location.state?.productId;
  const productIdFromQuery = searchParams.get("productId");

  const productSlugFromState =
    location.state?.slug || location.state?.productSlug;
  const productSlugFromRoute = slugFromRoute;

  const productSlug = productSlugFromState || productSlugFromRoute || "";

  const matchPercentageFromState = location.state?.matchPercentage;
  const matchPercentageFromQuery = searchParams.get("matchPercentage");
  const variantIdFromQuery = searchParams.get("variant");

  const clickedMatchPercentage = useMemo(() => {
    const rawValue = matchPercentageFromState ?? matchPercentageFromQuery;

    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return null;
    }

    const parsed = Number(rawValue);

    return Number.isFinite(parsed) ? parsed : null;
  }, [matchPercentageFromState, matchPercentageFromQuery]);

  const shouldOpenTryOnModalFromRoute =
    location.state?.openTryOnModal === true ||
    searchParams.get("try_on") === "true";

  const productTypeFromState =
    location.state?.productType || location.state?.product_type;
  const productTypeFromQuery = searchParams.get("product_type");

  const productType = useMemo(() => {
    const rawType = productTypeFromState ?? productTypeFromQuery;
    if (isExclusiveSlug(productSlug) || rawType === "exclusive") return "exclusive";
    if (productSlug) {
      return isMarketplaceSlug(productSlug) ? "marketplace" : "product";
    }

    return rawType === "product" ? "product" : "marketplace";
  }, [productSlug, productTypeFromState, productTypeFromQuery]);

  const isExclusiveProduct = productType === "exclusive";
  const navbarLogoRedirectPath = isExclusiveProduct ? "/eftakhari-collection" : undefined;
  const isStandardCollectionProduct = productType === "product";
  const isCollectionProduct = isStandardCollectionProduct || isExclusiveProduct;
  const handleExclusiveBundleAdded = useCallback(
    ({ bundleId, bundleInstanceId }) => {
      setCartDrawerProductId(null);
      setCartDrawerBundleId(bundleId);
      setCartDrawerBundleInstanceId(
        bundleInstanceId,
      );
      setIsCartDrawerOpen(true);
    },
    [],
  );

  const {
    bundleDetails: exclusiveBundleDetails,
    bundleProducts: exclusiveBundleProducts,
    isBundleLoading,
    isSizingModalOpen: isExclusiveBundleSizingModalOpen,
    sizingModalMode: exclusiveBundleSizingModalMode,
    openBundleModal: openExclusiveBundleModal,
    closeBundleModal: closeExclusiveBundleModal,
    confirmBundle: confirmExclusiveBundle,
  } = useExclusiveBundleFlow({ enabled: isExclusiveProduct, onBundleAdded: handleExclusiveBundleAdded });


  const updateCollectionVariantQueryParam = useCallback(
    (variantValue) => {
      setSearchParams((currentParams) => {
          const nextParams = new URLSearchParams(currentParams);

          if (variantValue !== null &&
            variantValue !== undefined &&
            variantValue !== ""
          ) {
            nextParams.set("variant", String(variantValue));
          } else {
            nextParams.delete("variant");
          }

          return nextParams;
        },
        { replace: true },
      )},
    [setSearchParams],
  );
  const productId = useMemo(() => {
    if (productSlug) return null;

    const resolvedId = productIdFromState ?? productIdFromQuery;
    const parsed = Number(resolvedId);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [productSlug, productIdFromState, productIdFromQuery]);

  const hasProductLookup = productSlug
    ? Boolean(productSlug)
    : Boolean(productId);
  const [selectedVariantState, setSelectedVariantState] = useState({id: null, size: ""});
  const hasInitializedVariantRef = useRef(false);
  const hasUserSelectedSizeRef = useRef(false);
  const pendingManualVariantRef = useRef("");
  const shouldPreferRecommendedAfterAuthRef = useRef(false);
  const postLoginRecommendationStartedRef = useRef(false);
  const [marketplaceSelectedSize, setMarketplaceSelectedSize] = useState("");
  const selectedSize = isCollectionProduct ? selectedVariantState.size : marketplaceSelectedSize;
  const selectedVariantId = selectedVariantState.id;
  const [isAdded, setIsAdded] = useState(false);

  const [openDetailSections, setOpenDetailSections] = useState({
    Description: false,
    "Materials & Care": false,
    "Fit & Sizing": false,
    "Shipping & Returns": false,
  });

  const toggleDetailSection = (title) => {
    setOpenDetailSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };
  const productQueryArg = hasProductLookup
    ? {
        ...(productSlug
          ? {
              slug: productSlug,
              product_type: productType,
            }
          : {
              product_id: productId,
              product_type: productType,
            }),
        ...(clickedMatchPercentage !== null &&
        clickedMatchPercentage !== undefined
          ? { match_percentage: clickedMatchPercentage }
          : {}),
      }
    : undefined;

  const topProductsRequestKey = [
    productType,
    productSlug || productId || "",
    shouldUseSignedMarketplaceApis ? "signed" : "public",
  ].join(":");

  const [topProductsPagination, setTopProductsPagination] = useState({
    requestKey: topProductsRequestKey,
    page: 1,
  });

  const topProductsPage = topProductsPagination.requestKey === topProductsRequestKey
      ? topProductsPagination.page
      : 1;

  const topProductsLoadMoreRef = useRef(null);

  useEffect(() => {
    setTopProductsPagination({
      requestKey: topProductsRequestKey,
      page: 1,
    });
  }, [topProductsRequestKey]);

  const paginatedTopProductsQueryArg = productQueryArg
    ? {
        ...productQueryArg,
        page: topProductsPage,
        page_size: TOP_PRODUCTS_PAGE_SIZE,
      }
    : undefined;

  const {
    data: productDetailsResponse,
    isLoading: isDetailsLoading,
    isFetching: isDetailsFetching,
    isError: isDetailsError,
    error: productDetailsError,
  } = useMarketplaceProductDetailsCachedQuery(productQueryArg, {
    skip: !hasProductLookup,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: publicTopProductsResponse,
    isLoading: isPublicTopProductsLoading,
    isFetching: isPublicTopProductsFetching,
    isError: isPublicTopProductsError,
  } = usePublicTopProductsCachedQuery(paginatedTopProductsQueryArg, {
    skip:
      !hasProductLookup ||
      isCollectionProduct ||
      shouldUseSignedMarketplaceApis,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: userTopProductsResponse,
    isLoading: isUserTopProductsLoading,
    isFetching: isUserTopProductsFetching,
    isError: isUserTopProductsError,
  } = useUserTopProductsCachedQuery(paginatedTopProductsQueryArg, {
    skip:
      !hasProductLookup ||
      isCollectionProduct ||
      !shouldUseSignedMarketplaceApis,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  
  const visibleProductLookupKey = productSlug || productId;
  const guestProductMatchKey = String(visibleProductLookupKey || "");

  const activateGuestMatchForCurrentProduct = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    clearSessionTokenIfLocalTokenMissing();
    const latestToken = getLocalPendingSurveyToken();
    setPendingSurveyToken(latestToken);
    if (!latestToken || !guestProductMatchKey) {
      return;
    }

    const savedProductKey = getGuestPublicMatchProduct(latestToken);
    if (savedProductKey) {
      setGuestPublicMatchProductKey(savedProductKey);
      return;
    }

    markGuestPublicMatchProduct({
      token: latestToken,
      productKey: guestProductMatchKey,
    });

    setGuestPublicMatchProductKey(guestProductMatchKey);
    setActiveGuestMatchProductKey(guestProductMatchKey);
  }, [guestProductMatchKey]);

  const signedMatchStreamRequestKey = useMemo(() => {
    if (!shouldUseSignedMarketplaceApis || !visibleProductLookupKey) {
      return "";
    }

    return `${productType}:${visibleProductLookupKey}:${authTick}`;
  }, [
    shouldUseSignedMarketplaceApis,
    visibleProductLookupKey,
    productType,
    authTick,
  ]);

  const matchStreamQueryArg = productQueryArg
    ? {
        ...productQueryArg,
        ...(shouldUseSignedMarketplaceApis && signedMatchStreamRequestKey
          ? { request_key: signedMatchStreamRequestKey }
          : {}),
        ...(!isAuthenticated && pendingSurveyToken
          ? { token: pendingSurveyToken }
          : {}),
      }
    : undefined;

  const [guestPublicMatchProductKey, setGuestPublicMatchProductKey] = useState(
    () => getGuestPublicMatchProduct(getLocalPendingSurveyToken()),
  );

  const [activeGuestMatchProductKey, setActiveGuestMatchProductKey] = useState("");
  const previousPendingSurveyTokenRef = useRef("");

  useEffect(() => {
    if (!pendingSurveyToken) {
      previousPendingSurveyTokenRef.current = "";
      setGuestPublicMatchProductKey("");
      setActiveGuestMatchProductKey("");
      return;
    }

    const savedProductKey = getGuestPublicMatchProduct(pendingSurveyToken);
    if (savedProductKey) {
      setGuestPublicMatchProductKey(savedProductKey);
      previousPendingSurveyTokenRef.current = pendingSurveyToken;
      return;
    }

    if (!guestProductMatchKey) {
      return;
    }
    markGuestPublicMatchProduct({
      token: pendingSurveyToken,
      productKey: guestProductMatchKey,
    });

    setGuestPublicMatchProductKey(guestProductMatchKey);
    setActiveGuestMatchProductKey(guestProductMatchKey);

    previousPendingSurveyTokenRef.current = pendingSurveyToken;
  }, [pendingSurveyToken, guestProductMatchKey]);

  const canShowGuestMatchAnalysis =
    !isAuthenticated &&
    hasPendingSurveyToken &&
    guestProductMatchKey &&
    (
      activeGuestMatchProductKey === guestProductMatchKey ||
      guestPublicMatchProductKey === guestProductMatchKey
    );

  const canShowMatchAnalysis = !isExclusiveProduct && (shouldUseSignedMarketplaceApis || canShowGuestMatchAnalysis);
  const isSurveySectionVisible = !isExclusiveProduct && !canShowMatchAnalysis && !hasPendingSurveyToken;
  const shouldRequestGuestPublicMatch = !isExclusiveProduct && canShowGuestMatchAnalysis && hasProductLookup;

  const {
    currentData: userMatchScoreResponse,
    isLoading: isUserMatchLoading,
    isFetching: isUserMatchFetching,
    isError: isUserMatchError,
  } = useUserSingleMarketplaceProductStreamCachedQuery(matchStreamQueryArg, {
    skip: isExclusiveProduct || !hasProductLookup || (!shouldUseSignedMarketplaceApis && !shouldRequestGuestPublicMatch)
    || (shouldUseSignedMarketplaceApis && !signedMatchStreamRequestKey),
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: publicCollectionTopProductsResponse,
    isLoading: isPublicCollectionTopProductsLoading,
    isFetching: isPublicCollectionTopProductsFetching,
    isError: isPublicCollectionTopProductsError,
  } = usePublicCollectionTopProductsCachedQuery(productQueryArg, {
    skip:
      !hasProductLookup ||
      !isStandardCollectionProduct ||
      shouldUseSignedMarketplaceApis,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const {
    data: userCollectionTopProductsResponse,
    isLoading: isUserCollectionTopProductsLoading,
    isFetching: isUserCollectionTopProductsFetching,
    isError: isUserCollectionTopProductsError,
  } = useUserCollectionTopProductsCachedQuery(productQueryArg, {
    skip:
      !hasProductLookup ||
      !isStandardCollectionProduct ||
      !shouldUseSignedMarketplaceApis,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: exclusiveTopProductsResponse,
    isLoading: isExclusiveTopProductsLoading,
    isFetching: isExclusiveTopProductsFetching,
    isError: isExclusiveTopProductsError,
  } = useExclusiveTopProductsCachedQuery(productQueryArg, {
    skip:
      !hasProductLookup ||
      !isExclusiveProduct,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const product = useMemo(
    () => normalizeProductDetails(productDetailsResponse, productType),
    [productDetailsResponse, productType],
  );

  const safeProduct = product || {
    id: productId || null,
    name: "",
    url: "",
    slug: "",
    real_slug: "",
    image: "",
    image_url: "",
    images: [],
    main_images: [],
    price: "",
    original_price: "",
    originalPrice: 0,
    discounted_price: "",
    discountedPrice: 0,
    discount: 0,
    discountPercent: 0,
    savings: 0,
    hasDiscount: false,
    garmentType: "",
    description: "",
    metaTitle: null,
    metaDescription: null,
    keywords: [],
    brand: "",
    color: "",
    is_marketplace: !isCollectionProduct,
    productType,
    product_type: productType,
    marketplace_url: "",
    quantity: null,
    isOutOfStock: false,
    variants: [],
  };

  const cartProductLookupId =
    isCollectionProduct && selectedVariantId
      ? Number(selectedVariantId)
      : safeProduct?.id;

  const isInCart = useSelector(selectIsInCart(cartProductLookupId));

  const standaloneCartItems = useSelector(selectStandaloneCartItems);
  const isExclusiveStandaloneInCart = useMemo(() => {

    if (!isExclusiveProduct || !cartProductLookupId) return false;
    const lookupId = Number(cartProductLookupId);
    if (!Number.isFinite(lookupId) || lookupId <= 0) return false;

    return standaloneCartItems.some((item) => {
      const cartProduct = item?.product;
      const cartVariantId = Number(cartProduct?.variant_id ?? cartProduct?.id);
      return cartVariantId === lookupId;
    });
  }, [
    isExclusiveProduct,
    standaloneCartItems,
    cartProductLookupId,
  ]);

  const effectiveIsInCart = isExclusiveProduct ? isExclusiveStandaloneInCart : isInCart;
  const collectionVariants = useMemo(() => {
    if (!isCollectionProduct) return [];

    return Array.isArray(safeProduct?.variants) ? safeProduct.variants : [];
  }, [isCollectionProduct, safeProduct?.variants]);

  const snapSeoProduct = productSlug ? productSeoMap[productSlug] : null;
  const seoProduct = {
    name: safeProduct?.name || snapSeoProduct?.name || "",
    garmentType: safeProduct?.garmentType || snapSeoProduct?.category || "",
    brand: safeProduct?.brand || snapSeoProduct?.brand || "",
    price: safeProduct?.price || snapSeoProduct?.price || "",
    image: safeProduct?.image || snapSeoProduct?.image || "",
    description: safeProduct?.description || snapSeoProduct?.description || "",
  };

  const cleanSeoPart = (value) =>
    String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/gi, '"')
      .replace(/\s+/g, " ")
      .trim();

  const productName = cleanSeoPart(seoProduct.name);
  const productCategory = cleanSeoPart(seoProduct.garmentType);
  const productBrand = cleanSeoPart(seoProduct.brand);

  const fallbackSeoTitle = [productName || "Product", productCategory, productBrand]
    .filter(Boolean)
    .join(" | ");

  const fallbackSeoDescription = cleanSeoPart(seoProduct.description);
  const seoTitle = safeProduct?.metaTitle !== null &&
    safeProduct?.metaTitle !== undefined
      ? cleanSeoPart(safeProduct.metaTitle)
      : fallbackSeoTitle;
  const seoDescription = safeProduct?.metaDescription !== null &&
    safeProduct?.metaDescription !== undefined
      ? cleanSeoPart(safeProduct.metaDescription)
      : fallbackSeoDescription;

  const uniqueCollectionVariants = useMemo(() => {
    const variantsBySize = new Map();

    collectionVariants.forEach((variant) => {
      const size = String(variant?.size || "")
        .trim()
        .toUpperCase();

      if (!size) return;
      const existingVariant = variantsBySize.get(size);
      if (!existingVariant) {
        variantsBySize.set(size, variant);
        return;
      }
      if (
        existingVariant?.isOutOfStock &&
        !variant?.isOutOfStock
      ) {
        variantsBySize.set(size, variant);
      }
    });
    return Array.from(variantsBySize.values());
  }, [collectionVariants]);

  const selectedCollectionVariant = collectionVariants.find(
    (variant) => Number(variant.id) === Number(selectedVariantId),
  );

  const selectedVariantQuantity =
  isCollectionProduct
    ? normalizeStockQuantity(
        selectedCollectionVariant?.quantity,
      )
    : null;

  const isSelectedVariantOutOfStock =
    isCollectionProduct &&
    Boolean(selectedCollectionVariant) &&
    selectedVariantQuantity !== null &&
    selectedVariantQuantity <= 0;

  const areAllCollectionVariantsOutOfStock =
    isCollectionProduct &&
    collectionVariants.length > 0 &&
    collectionVariants.every(
      (variant) => variant?.isOutOfStock === true,
    );

  const isCollectionProductOutOfStock =
    isCollectionProduct &&
    (
      isSelectedVariantOutOfStock ||
      (
        !selectedCollectionVariant &&
        areAllCollectionVariantsOutOfStock
      ) ||
      (
        !collectionVariants.length &&
        safeProduct?.isOutOfStock === true
      )
    );

  const selectedProductPricing = useMemo(() => {
    if (!isCollectionProduct) {
      return getProductPricing(safeProduct, false);
    }

    if (!selectedCollectionVariant) {
      return getProductPricing(
        {
          price:
            safeProduct?.original_price ??
            safeProduct?.price,

          original_price:
            safeProduct?.original_price ??
            safeProduct?.price,

          discounted_price:
            safeProduct?.discounted_price,

          discount:
            safeProduct?.discount ??
            safeProduct?.discountPercent,
        },
        true,
      );
    }

  const hasVariantDiscountedPrice =
    selectedCollectionVariant?.discounted_price !== null &&
    selectedCollectionVariant?.discounted_price !== undefined &&
    selectedCollectionVariant?.discounted_price !== "";

  const hasVariantDiscount =
    selectedCollectionVariant?.discount !== null &&
    selectedCollectionVariant?.discount !== undefined &&
    selectedCollectionVariant?.discount !== "";

  return getProductPricing(
    {
      price:
        selectedCollectionVariant?.original_price ??
        selectedCollectionVariant?.price ??
        safeProduct?.original_price ??
        safeProduct?.price,

      original_price:
        selectedCollectionVariant?.original_price ??
        selectedCollectionVariant?.price ??
        safeProduct?.original_price ??
        safeProduct?.price,

      discounted_price: hasVariantDiscountedPrice
        ? selectedCollectionVariant.discounted_price
        : safeProduct?.discounted_price,

      discount: hasVariantDiscount
        ? selectedCollectionVariant.discount
        : safeProduct?.discount ??
          safeProduct?.discountPercent,
    },
    true,
  );
}, [
  isCollectionProduct,
  selectedCollectionVariant,
  safeProduct?.price,
  safeProduct?.original_price,
  safeProduct?.discounted_price,
  safeProduct?.discount,
  safeProduct?.discountPercent,
]);

  // Express checkout buys the selected variant on its own, bypassing the cart.
  const quickPayItems = useMemo(() => {
    if (!isCollectionProduct || !selectedVariantId) return [];
    return [{ variant_id: Number(selectedVariantId), quantity: 1 }];
  }, [isCollectionProduct, selectedVariantId]);

  // Cents, and it must match what the backend recomputes from the DB, so it
  // uses the same discounted price the page is displaying.
  const quickPayAmount = useMemo(
    () => Math.round(Number(selectedProductPricing?.discountedPrice || 0) * 100),
    [selectedProductPricing],
  );

  const [quickPayButtonHeight, setQuickPayButtonHeight] = useState(() => {
    if (typeof window === "undefined") return 46;
    return window.matchMedia("(min-width: 640px)").matches ? 46 : 40;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(min-width: 640px)");

    const handleChange = (event) => {
      setQuickPayButtonHeight(event.matches ? 46 : 40);
    };

    setQuickPayButtonHeight(mediaQuery.matches ? 46 : 40);
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  const displaySizeOptions = isCollectionProduct
    ? uniqueCollectionVariants
    : sizeOptions;

  const relatedProducts = useMemo(() => {
    const response = isExclusiveProduct
      ? exclusiveTopProductsResponse
      : isStandardCollectionProduct
      ? shouldUseSignedMarketplaceApis
        ? userCollectionTopProductsResponse
        : publicCollectionTopProductsResponse
      : shouldUseSignedMarketplaceApis
        ? userTopProductsResponse
        : publicTopProductsResponse;

    return normalizeTopProducts(response, productType);
  }, [
    isExclusiveProduct,
    isStandardCollectionProduct,
    shouldUseSignedMarketplaceApis,
    exclusiveTopProductsResponse,
    userCollectionTopProductsResponse,
    publicCollectionTopProductsResponse,
    userTopProductsResponse,
    publicTopProductsResponse,
    productType,
  ]);

  const matchData = useMemo(() => {
    if (!canShowMatchAnalysis) {
      return emptyMatchData;
    }

    const normalizedResponse = normalizeMatchScore(userMatchScoreResponse);

    if (!normalizedResponse) {
      return {
        ...emptyMatchData,
        matchPercentage:
          clickedMatchPercentage !== null &&
          clickedMatchPercentage !== undefined
            ? Math.round(Number(clickedMatchPercentage))
            : null,
      };
    }

    return {
      recommendedSize: normalizedResponse.recommendedSize ?? null,
      matchPercentage:
        normalizedResponse.matchPercentage ??
        (clickedMatchPercentage !== null &&
        clickedMatchPercentage !== undefined
          ? Math.round(Number(clickedMatchPercentage))
          : null),
      matchSummary: normalizedResponse.matchSummary || "",
      tags: Array.isArray(normalizedResponse.tags)
        ? normalizedResponse.tags
        : [],
    };
  }, [
    canShowMatchAnalysis,
    userMatchScoreResponse,
    clickedMatchPercentage,
  ]);

  const normalizedRecommendedSize = useMemo(
    () => String(matchData?.recommendedSize || "").trim().toUpperCase(),
  [matchData?.recommendedSize],
  );

  const isMatchStreamInProgress = canShowMatchAnalysis &&
    (isUserMatchLoading || isUserMatchFetching ||
      Boolean(userMatchScoreResponse && !userMatchScoreResponse.done));

  const isRecommendedSizeLoading = isMatchStreamInProgress && !normalizedRecommendedSize;

  useEffect(() => {
    if (!shouldPreferRecommendedAfterAuthRef.current) return;
    if (!shouldUseSignedMarketplaceApis) return;
    if (isUserMatchLoading || isUserMatchFetching) postLoginRecommendationStartedRef.current = true;
  }, [
    shouldUseSignedMarketplaceApis,
    isUserMatchLoading,
    isUserMatchFetching,
  ]);

  const isMatchPercentageLoading = isMatchStreamInProgress &&
    (matchData.matchPercentage === null || matchData.matchPercentage === undefined);

  const isMatchSummaryLoading = isMatchStreamInProgress && !matchData.matchSummary;

  useEffect(() => {
    if (isAuthenticated) return;
    if (!pendingSurveyToken) return;
    if (!canShowGuestMatchAnalysis) return;
    if (!guestProductMatchKey) return;
    if (!userMatchScoreResponse?.done) return;
    if (userMatchScoreResponse?.error) return;

    markGuestPublicMatchProduct({
      token: pendingSurveyToken,
      productKey: guestProductMatchKey,
    });
    setGuestPublicMatchProductKey(guestProductMatchKey);
  }, [
    isAuthenticated,
    pendingSurveyToken,
    canShowGuestMatchAnalysis,
    guestProductMatchKey,
    userMatchScoreResponse?.done,
    userMatchScoreResponse?.error,
  ]);

  const [saveBoardProduct, setSaveBoardProduct] = useState(null);
  const [isSaveBoardModalOpen, setIsSaveBoardModalOpen] = useState(false);
  useEffect(() => {
    setIsAdded(effectiveIsInCart);
  }, [effectiveIsInCart]);


  const handleAddToCart = (options = {}) => {
    const showSuccessToast = options?.showSuccessToast !== false;
    if (!safeProduct?.id || isProductDetailsLoading) {
      toast.error("Product details are not available yet.");
      return false;
    }

    const selectedVariant =
      isCollectionProduct && collectionVariants.length
        ? selectedCollectionVariant
        : null;

    if (isCollectionProduct && collectionVariants.length && !selectedVariant) {
      toast.error("Please select a size.");
      return false;
    }

    if (isCollectionProduct && !collectionVariants.length) {
      toast.error("No product variants are available.");
      return false;
    }

    const selectedVariantQuantity = normalizeStockQuantity(selectedVariant?.quantity);

    if (
      isCollectionProduct &&
      selectedVariantQuantity !== null &&
      selectedVariantQuantity <= 0
    ) {
      toast.error(`${selectedVariant?.size || "This size"} is out of stock.`);
      return false;
    }

    const cartPricing = selectedProductPricing;
    const numericPrice = cartPricing.discountedPrice;

    const cartItemId = isCollectionProduct
      ? Number(selectedVariant.id)
      : Number(safeProduct.id);

    const cartProduct = {
      ...safeProduct,
      id: cartItemId,
      product_id: safeProduct.id,
      variant_id: isCollectionProduct
        ? Number(selectedVariant.id)
        : Number(safeProduct.id),
      image:
        isCollectionProduct && selectedVariant?.image
          ? selectedVariant.image
          : selectedProductImage || safeProduct.image,

      available_quantity: isCollectionProduct
        ? selectedVariant?.quantity
        : safeProduct?.quantity,
      price: numericPrice,
        ...(isCollectionProduct
      ? {
          original_price:
            cartPricing.originalPrice,

          discounted_price:
            cartPricing.discountedPrice,

          discount:
            cartPricing.discountPercent,

          discount_percent:
            cartPricing.discountPercent,

          savings:
            cartPricing.savings,

          variant_original_price:
            selectedVariant?.originalPrice ??
            cartPricing.originalPrice,

          variant_discounted_price:
            selectedVariant?.discountedPrice ??
            cartPricing.discountedPrice,

          variant_discount:
            selectedVariant?.discountPercent ??
            cartPricing.discountPercent,
        }
      : {}),
      size: isCollectionProduct ? selectedVariant.size : selectedSize,
      color: isCollectionProduct
        ? selectedVariant.color || safeProduct.color || ""
        : safeProduct.color || "",
      main_images: productImages,
      productType,
      product_type: productType,
      is_marketplace: !isCollectionProduct,
    };
    setCartDrawerBundleId(null);
    setCartDrawerBundleInstanceId(null);
    setCartDrawerProductId(cartProduct.id);
    setIsCartDrawerOpen(true);

    try {
      logAddToCart({
        id: cartProduct.id,
        name: cartProduct.name,
        price: numericPrice,
        category: cartProduct.garmentType || "Fashion",
        variant: cartProduct.variant_id,
        size: cartProduct.size,
        quantity: 1,
      });
    } catch (error) {
      console.log("Analytics add to cart logging failed", error);
    }

    dispatch(
      addToCart({
        product: cartProduct,
        quantity: 1,
      }),
    );
    setCartDrawerProductId(cartProduct.id);
    setIsCartDrawerOpen(true);

    setIsAdded(true);
    if (showSuccessToast) {
      toast.success("Added to cart");
    }
    return true;    
  };
  useEffect(() => {
    const handleAuthChanged = () => {
      const isNowAuthenticated = Boolean(localStorage.getItem("authToken"));
      if (isNowAuthenticated) {
        shouldPreferRecommendedAfterAuthRef.current = true;
        postLoginRecommendationStartedRef.current = false;

        hasUserSelectedSizeRef.current = false;
        pendingManualVariantRef.current = "";
        hasInitializedVariantRef.current = false;

        setSelectedVariantState({ id: null, size: "" });
        setMarketplaceSelectedSize("");
      }
      setAuthTick((prev) => prev + 1);
    };

    window.addEventListener("eveness-auth-changed", handleAuthChanged);

    return () => {
      window.removeEventListener("eveness-auth-changed", handleAuthChanged);
    };
  }, []);

  useEffect(() => {
    if (!hasProductLookup) return;

    window.scrollTo(0, 0);
    setSelectedImageIndex(0);
    hasInitializedVariantRef.current = false;
    hasUserSelectedSizeRef.current = false;
    pendingManualVariantRef.current = "";
    setSelectedVariantState({ id: null, size: "" });
    setMarketplaceSelectedSize("");
    setTryOnImage("");
    resetMultiTryOnStream();
    completedMultiTryOnRequestRef.current = "";
    if (isExclusiveProduct) {
      setIsTryOnModalOpen(false);
    } else if (shouldOpenTryOnModalFromRoute && !isAuthenticated) {
      toast.error("Please sign in to use this feature.");
      setIsTryOnModalOpen(false);
    } else {
      setIsTryOnModalOpen(Boolean(shouldOpenTryOnModalFromRoute));
    }
    setUploadedTryOnFile(null);
    setUploadedTryOnPreview("");
    setSelectedTryOnSource("");

    setOpenDetailSections({
      Description: false,
      "Materials & Care": false,
      "Fit & Sizing": false,
      "Shipping & Returns": false,
    });
  }, [
    hasProductLookup,
    productId,
    productSlug,
    productType,
    isExclusiveProduct,
    shouldOpenTryOnModalFromRoute,
    isAuthenticated,
    resetMultiTryOnStream,
  ]);
  const productImages = safeProduct.images?.length
    ? safeProduct.images
    : safeProduct.image
      ? [safeProduct.image]
      : [];

  const originalSelectedProductImage =
    productImages[selectedImageIndex] || safeProduct.image || "";

  const selectedProductImage = tryOnImage || originalSelectedProductImage;
  const selectedProductImageFailed = selectedProductImage && failedProductImages.has(selectedProductImage);

  useEffect(() => {
    const finalImage = multiTryOnStream?.final_image;
    const requestId = multiTryOnStream?.request_id;

    if (!finalImage || !requestId) return;
    if (completedMultiTryOnRequestRef.current === requestId) return;

    completedMultiTryOnRequestRef.current = requestId;
    setTryOnImage(finalImage);
    toast.success("Virtual try-on completed.");
  }, [multiTryOnStream?.final_image, multiTryOnStream?.request_id]);
  const openSurveyGate = () => {
    closeChatModal();
    if (hasPendingSurveyToken) {
      if (!isAuthenticated) {
        openLoginModal();
        return;
      }
      toast("Your profile is being updated. Please wait a moment.");
      refetchProfile?.();
      return;
    }

    if (!isAuthenticated) {
      setRootPersonalizationOpen(true);
      return;
    }

    if (!hasCompletedSurvey) {
      setRootPersonalizationOpen(true);
    }
  };

  const startSurveyFlow = (initialStep = 1) => {
    setRootPersonalizationOpen(false);
    setAuthModalOpen(false);
    setIsFitScoreAccountFlow(false);
    setHomeProfileInitialStep(initialStep);
    setIsHomeProfileFlowOpen(true);
  };

  const openSaveToBoardModal = (item) => {
      if (!item) {
        toast.error("Product details are not available yet.");
        return;
      }

      const resolvedProductType =
        item?.productType ||
        item?.product_type ||
        (item?.is_marketplace === false ? "product" : "marketplace");

      const resolvedSlug =
        item?.real_slug ||
        item?.slug ||
        item?.productSlug ||
        item?.product_url ||
        safeProduct?.slug ||
        productSlug ||
        "";

      if (!resolvedSlug) {
        toast.error("Product slug is not available yet.");
        return;
      }

      const isMainProductSave = item === safeProduct;

      const visibleMatchPercentage = isMainProductSave
        ? matchData?.matchPercentage !== null &&
          matchData?.matchPercentage !== undefined &&
          matchData?.matchPercentage !== ""
          ? Number(matchData.matchPercentage)
          : null
        : getVisibleCardMatchPercentage(item);

      setSaveBoardProduct({
        ...item,
        slug: resolvedSlug,
        real_slug: resolvedSlug,
        productType: resolvedProductType,
        product_type: resolvedProductType,
        matchPercentage: visibleMatchPercentage,
        match_percentage: visibleMatchPercentage,
        image: item?.image || item?.image_url || safeProduct?.image || "",
        image_url: item?.image_url || item?.image || safeProduct?.image || "",
        name: item?.name || item?.product_name || safeProduct?.name || "Product",
        price: formatProductPrice(
          item?.original_price ??
            item?.price ??
            safeProduct?.original_price ??
            safeProduct?.price,
        ),

        original_price:
          item?.original_price ??
          item?.price ??
          safeProduct?.original_price ??
          safeProduct?.price ??
          "",

        discounted_price:
          item?.discounted_price ??
          safeProduct?.discounted_price ??
          "",

        discount:
          item?.discount ??
          item?.discount_percent ??
          safeProduct?.discount ??
          safeProduct?.discountPercent ??
          0,

        discountPercent:
          item?.discountPercent ??
          item?.discount ??
          item?.discount_percent ??
          safeProduct?.discountPercent ??
          safeProduct?.discount ??
          0,        
        garmentType:
          item?.garmentType ||
          item?.garment_type ||
          safeProduct?.garmentType ||
          "",
      });

      setIsSaveBoardModalOpen(true);
    };

    const handleSaveBoard = (item = safeProduct) => {
      if (!isAuthenticated) {
        toast.error("Please sign in to save this product.");
        openLoginModal();
        return;
      }

      if (!hasCompletedSurvey) {
        if (hasPendingSurveyToken) {
          toast(
            "Your profile is being updated. Please wait a moment.",
          );

          refetchProfile?.();
          return;
        }
        toast.error("Please complete your survey before saving this product.");
        setRootPersonalizationOpen(true);
        return;
      }

      openSaveToBoardModal(item);
    };
  const isTopProductsLoading = isExclusiveProduct ? (
      isExclusiveTopProductsLoading ||
      isExclusiveTopProductsFetching
    )
    : isStandardCollectionProduct
    ? shouldUseSignedMarketplaceApis
      ? (isUserCollectionTopProductsLoading ||
        isUserCollectionTopProductsFetching
        )
      : (isPublicCollectionTopProductsLoading ||
        isPublicCollectionTopProductsFetching
        )
    : shouldUseSignedMarketplaceApis
      ? (isUserTopProductsLoading || (isUserTopProductsFetching && topProductsPage === 1))
      : (isPublicTopProductsLoading || (isPublicTopProductsFetching && topProductsPage === 1));

  const [visibleRecommendationsProductId, setVisibleRecommendationsProductId] =
    useState(null);

  const [
    authenticatedSurveyRecommendationsKey,
    setAuthenticatedSurveyRecommendationsKey,
  ] = useState(null);

  const [
    isWaitingForAuthenticatedSurveyRecommendations,
    setIsWaitingForAuthenticatedSurveyRecommendations,
  ] = useState(false);

  const visibleRecommendationsLookupKey = productSlug || productId;

  const authenticatedSignedRecommendationsResponse =isExclusiveProduct
      ? exclusiveTopProductsResponse
      : isStandardCollectionProduct
        ? userCollectionTopProductsResponse
        : userTopProductsResponse;

  const hasAuthenticatedSignedRecommendationsResponse =
    authenticatedSignedRecommendationsResponse !== undefined &&
    authenticatedSignedRecommendationsResponse !== null;

  const canShowAuthenticatedSurveyRecommendations =
    authenticatedSurveyRecommendationsKey ===
    visibleRecommendationsLookupKey;

  const shouldHideRecommendationCards =
    !canShowAuthenticatedSurveyRecommendations &&
    (
      isTopProductsLoading ||
      visibleRecommendationsProductId !== visibleRecommendationsLookupKey
    );

  const displayRelatedProducts = shouldHideRecommendationCards
    ? []
    : relatedProducts;
  const isTopProductsError = isExclusiveProduct
    ? isExclusiveTopProductsError
    : isStandardCollectionProduct
    ? shouldUseSignedMarketplaceApis
      ? isUserCollectionTopProductsError
      : isPublicCollectionTopProductsError
    : shouldUseSignedMarketplaceApis
      ? isUserTopProductsError
      : isPublicTopProductsError;

  const isMarketplaceRecommendations =productType === "marketplace";
  const activeTopProductsResponse = shouldUseSignedMarketplaceApis ? userTopProductsResponse : publicTopProductsResponse;
  const isActiveTopProductsFetching = shouldUseSignedMarketplaceApis ? isUserTopProductsFetching : isPublicTopProductsFetching;
  const topProductsHasMore = isMarketplaceRecommendations && Boolean(activeTopProductsResponse?.has_more);

  const topProductsNextPage =
    activeTopProductsResponse?.next_page !== null &&
    activeTopProductsResponse?.next_page !== undefined
      ? Number(activeTopProductsResponse.next_page)
      : null;

  const isLoadingMoreTopProducts =
    isMarketplaceRecommendations &&
    topProductsPage > 1 &&
    isActiveTopProductsFetching;

  useEffect(() => {
    if (!isMarketplaceRecommendations) return;
    if (shouldHideRecommendationCards) return;
    if (displayRelatedProducts.length === 0) return;
    if (!topProductsHasMore) return;
    if (isActiveTopProductsFetching) return;
    if (!Number.isFinite(topProductsNextPage) || topProductsNextPage <= topProductsPage) return;
    if (typeof IntersectionObserver === "undefined") return;

    const loadMoreNode = topProductsLoadMoreRef.current;
    if (!loadMoreNode) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;

        setTopProductsPagination((current) => {
          const currentPage =
            current.requestKey === topProductsRequestKey
              ? current.page
              : 1;

          if (topProductsNextPage <= currentPage) return current;

          return {
            requestKey: topProductsRequestKey,
            page: topProductsNextPage,
          };
        });
      },
      {
        root: null,
        rootMargin: "600px 0px",
        threshold: 0.01,
      },
    );
    observer.observe(loadMoreNode);
    return () => {
      observer.disconnect();
    };
  }, [
    isMarketplaceRecommendations,
    shouldHideRecommendationCards,
    displayRelatedProducts.length,
    topProductsHasMore,
    isActiveTopProductsFetching,
    topProductsNextPage,
    topProductsPage,
    topProductsRequestKey,
  ]);

  useEffect(() => {
    if (!isWaitingForAuthenticatedSurveyRecommendations) return;
    if (!isAuthenticated) return;
    if (!shouldUseSignedMarketplaceApis) return;
    if (!hasProductLookup) return;
    if (!hasAuthenticatedSignedRecommendationsResponse) return;

    setAuthenticatedSurveyRecommendationsKey(
      visibleRecommendationsLookupKey,
    );

    setVisibleRecommendationsProductId(
      visibleRecommendationsLookupKey,
    );

    setIsWaitingForAuthenticatedSurveyRecommendations(false);
  }, [
    isWaitingForAuthenticatedSurveyRecommendations,
    isAuthenticated,
    shouldUseSignedMarketplaceApis,
    hasProductLookup,
    hasAuthenticatedSignedRecommendationsResponse,
    visibleRecommendationsLookupKey,
  ]);

  useEffect(() => {
    if (
      authenticatedSurveyRecommendationsKey &&
      authenticatedSurveyRecommendationsKey !==
        visibleRecommendationsLookupKey
    ) {
      setAuthenticatedSurveyRecommendationsKey(null);
      setIsWaitingForAuthenticatedSurveyRecommendations(false);
    }
  }, [
    authenticatedSurveyRecommendationsKey,
    visibleRecommendationsLookupKey,
  ]);

  useEffect(() => {
    if (isTopProductsLoading) return;
    if (isTopProductsError) return;
    if (!hasProductLookup) return;

    setVisibleRecommendationsProductId(visibleRecommendationsLookupKey);
  }, [
    isTopProductsLoading,
    isTopProductsError,
    hasProductLookup,
    visibleRecommendationsLookupKey,
    relatedProducts,
  ]);
  const isProductDetailsLoading = isDetailsLoading || isDetailsFetching;
  const isAddToCartDisabled = isProductDetailsLoading ||
  (
    isCollectionProduct &&
    (
      !selectedCollectionVariant ||
      isCollectionProductOutOfStock
    )
  );

  useEffect(() => {
    if (!isCollectionProduct) {
      hasInitializedVariantRef.current = false;
      return;
    }
    if (isProductDetailsLoading) return;
    const pendingManualVariant = pendingManualVariantRef.current;
    if (pendingManualVariant) 
      if (String(variantIdFromQuery || "") === pendingManualVariant) pendingManualVariantRef.current = "";
      else return;

    if (!collectionVariants.length) {
      hasInitializedVariantRef.current = false;
      setSelectedVariantState((current) => {
        if (current.id === null && current.size === "") {
          return current;
        }
        return {
          id: null,
          size: "",
        };
      });
      return;
    }

    const variantFromQuery = collectionVariants.find(
      (variant) => Number(variant.id) === Number(variantIdFromQuery),
    );

    const recommendedVariant = normalizedRecommendedSize
      ? uniqueCollectionVariants.find(
          (variant) =>
            String(variant?.size || "").trim().toUpperCase() ===
            normalizedRecommendedSize,
        )
      : null;
    const shouldPreferRecommendedAfterAuth = shouldPreferRecommendedAfterAuthRef.current;
    if (shouldPreferRecommendedAfterAuth) {
      const authenticatedStreamStarted = postLoginRecommendationStartedRef.current ||
        isUserMatchLoading || isUserMatchFetching;

      if (!shouldUseSignedMarketplaceApis) return;
      if (!authenticatedStreamStarted) return;

      if (recommendedVariant) {
        const recommendedVariantId = Number(recommendedVariant.id);
        const recommendedVariantSize = String(recommendedVariant.size || "");
        hasInitializedVariantRef.current = true;
        setSelectedVariantState((current) => {
          if (Number(current.id) === recommendedVariantId && current.size === recommendedVariantSize) return current;
          return { id: recommendedVariantId, size: recommendedVariantSize };
        });

        if (String(variantIdFromQuery || "") !== String(recommendedVariantId)) {
          updateCollectionVariantQueryParam(recommendedVariantId);
          return;
        }

        shouldPreferRecommendedAfterAuthRef.current = false;
        postLoginRecommendationStartedRef.current = false;
        return;
      }

      const authenticatedStreamFinished = userMatchScoreResponse?.done === true || isUserMatchError;
      if (!authenticatedStreamFinished) return;
      shouldPreferRecommendedAfterAuthRef.current = false;
      postLoginRecommendationStartedRef.current = false;
    }

    if (!variantFromQuery && isRecommendedSizeLoading) return;
    const currentSelectedVariant = collectionVariants.find(
      (variant) => Number(variant.id) === Number(selectedVariantState.id),
    );

    const firstInStockVariant = uniqueCollectionVariants.find(
      (variant) => variant?.isOutOfStock !== true,
    );

    const autoRecommendedVariant = !hasUserSelectedSizeRef.current
      ? recommendedVariant
      : null;
    const nextVariant = hasInitializedVariantRef.current
      ? variantFromQuery ||
        autoRecommendedVariant ||
        currentSelectedVariant ||
        firstInStockVariant ||
        uniqueCollectionVariants[0]
      : variantFromQuery ||
        autoRecommendedVariant ||
        currentSelectedVariant ||
        firstInStockVariant ||
        uniqueCollectionVariants[0];

    if (!nextVariant) return;

    const nextVariantId = Number(nextVariant.id);
    const nextVariantSize = String(nextVariant.size || "");
    hasInitializedVariantRef.current = true;

    setSelectedVariantState((current) => {
      if (Number(current.id) === nextVariantId && current.size === nextVariantSize) return current;
      return { id: nextVariantId, size: nextVariantSize };
    });

    if ( String(variantIdFromQuery || "") !== String(nextVariantId)) {
      updateCollectionVariantQueryParam(nextVariantId);
    }
  }, [
    isCollectionProduct,
    isProductDetailsLoading,
    collectionVariants,
    uniqueCollectionVariants,
    selectedVariantState.id,
    variantIdFromQuery,
    normalizedRecommendedSize,
    isRecommendedSizeLoading,
    shouldUseSignedMarketplaceApis,
    isUserMatchLoading,
    isUserMatchFetching,
    isUserMatchError,
    userMatchScoreResponse?.done,
    updateCollectionVariantQueryParam,
  ]);

  useEffect(() => {
    if (isCollectionProduct || isProductDetailsLoading) return;
    const pendingManualVariant = pendingManualVariantRef.current;
    if (pendingManualVariant) {
      const currentQueryVariant = String(variantIdFromQuery || "")
        .trim()
        .toUpperCase();

      if (currentQueryVariant === pendingManualVariant.toUpperCase()) pendingManualVariantRef.current = "";
      else return;
    }

    const querySize = String(variantIdFromQuery || "")
      .trim()
      .toUpperCase();

    const validQuerySize = sizeOptions.includes(querySize)
      ? querySize
      : "";

    const validRecommendedSize = sizeOptions.includes(normalizedRecommendedSize)
      ? normalizedRecommendedSize
      : "";
    const shouldPreferRecommendedAfterAuth = shouldPreferRecommendedAfterAuthRef.current;

    if (shouldPreferRecommendedAfterAuth) {
      const authenticatedStreamStarted = postLoginRecommendationStartedRef.current ||
        isUserMatchLoading || isUserMatchFetching;

      if (!shouldUseSignedMarketplaceApis) return;
      if (!authenticatedStreamStarted) return;

      if (validRecommendedSize) {
        setMarketplaceSelectedSize((current) =>
          current === validRecommendedSize
            ? current
            : validRecommendedSize,
        );

        if (querySize !== validRecommendedSize) {
          updateCollectionVariantQueryParam(validRecommendedSize);
          return;
        }

        shouldPreferRecommendedAfterAuthRef.current = false;
        postLoginRecommendationStartedRef.current = false;
        return;
      }

      const authenticatedStreamFinished = userMatchScoreResponse?.done === true || isUserMatchError;
      if (!authenticatedStreamFinished) return;

      shouldPreferRecommendedAfterAuthRef.current = false;
      postLoginRecommendationStartedRef.current = false;
    }
    if (validQuerySize) {
      setMarketplaceSelectedSize((current) =>
        current === validQuerySize ? current : validQuerySize,
      );
      return;
    }

    if (isRecommendedSizeLoading) return;

    const nextSize = !hasUserSelectedSizeRef.current && validRecommendedSize
        ? validRecommendedSize
        : marketplaceSelectedSize || "M";

    setMarketplaceSelectedSize((current) => current === nextSize ? current : nextSize);
    if (String(variantIdFromQuery || "").trim().toUpperCase() !== nextSize) updateCollectionVariantQueryParam(nextSize);

  }, [
    isCollectionProduct,
    isProductDetailsLoading,
    variantIdFromQuery,
    normalizedRecommendedSize,
    isRecommendedSizeLoading,
    marketplaceSelectedSize,
    updateCollectionVariantQueryParam,
  ]);

  const isMatchSectionLoading = isMatchStreamInProgress;
  const isPageInvalid = !hasProductLookup;
  const styleTags = canShowMatchAnalysis
    ? matchData.tags
    : [safeProduct?.garmentType, "Versatile", "Comfortable"].filter(Boolean);

  const matchDescription = canShowMatchAnalysis
    ? matchData.matchSummary
    : "Complete your style survey to see personalized fit analysis based on your measurements and style preferences.";

  const productWebsite = getWebsiteDomain(safeProduct?.url);
  const isEvenessProduct =
    isCollectionProduct || isEvenessUrl(safeProduct?.url);

  const detailRows = [
    {
      title: "Description",
      content:
        safeProduct?.description ||
        "No description available for this product.",
      isHtml: isCollectionProduct,
    },
    {
      title: "Materials & Care",
      content: safeProduct?.garmentType || "Not available",
    },
    {
      title: "Fit & Sizing",
      content:
        !isCollectionProduct && safeProduct?.url
          ? "View original product page"
          : "Not available",
      link: !isCollectionProduct ? safeProduct?.url : "",
    },
    {
      title: "Shipping & Returns",
      content: isExclusiveProduct
        ? "No Return Policy: Please note all sales are final. We do not accept returns, exchanges, or refunds. We encourage you to review sizing guides and product details carefully before placing your order as this is a made to order product."
        : "Please check the original retailer page for shipping and return policy.",
    },
  ];

  const handleAskEvenessGpt = () => {
    if (isProfileChecking) {
      toast.loading("Checking your profile...");
      return;
    }

    if (!canUseRealChat) {
      openSurveyGate();
      return;
    }

    if (isProductTransferLoading || isGlobalChatStreaming) {
      return;
    }

    const transferResponse = String(matchDescription || "").trim();
    const productImageUrl = safeProduct?.image_url || safeProduct?.image || "";

    if (!transferResponse) {
      toast.error("Match analysis is not available yet.");
      return;
    }

    if (!safeProduct?.name) {
      toast.error("Product details are not available yet.");
      return;
    }

    if (!productImageUrl) {
      toast.error("Product image is not available.");
      return;
    }

    startProductConversation({
      text: `Score Analysis for ${safeProduct.name}`,
      imageUrl: productImageUrl,
      response: transferResponse,
    });
  };

  const handleChatBarStartSurvey = () => {
    closeChatModal();
    setAuthModalOpen(false);
    setRootPersonalizationOpen(false);
    setIsFitScoreAccountFlow(false);
    setHomeProfileInitialStep(0);
    setIsHomeProfileFlowOpen(true);
  };

  const handleHomeProfileComplete = async () => {
    setIsHomeProfileFlowOpen(false);
    setRootPersonalizationOpen(false);
    setVisibleRecommendationsProductId(null);

    if (isAuthenticated) {
      setAuthenticatedSurveyRecommendationsKey(null);
      setIsWaitingForAuthenticatedSurveyRecommendations(true);
      await refetchProfile?.();
      setAuthTick((prev) => prev + 1);
      window.dispatchEvent(new Event("eveness-auth-changed"));
      return;
    }
    syncPendingSurveyToken();
    window.dispatchEvent(new Event("eveness-survey-token-changed"));
  };

  const handleSizeSelection = useCallback(({ size, variantId }) => {
    shouldPreferRecommendedAfterAuthRef.current = false;
    postLoginRecommendationStartedRef.current = false;
    hasUserSelectedSizeRef.current = true;
    if (!isCollectionProduct) {
      const normalizedSize = String(size || "")
        .trim()
        .toUpperCase();
      if (!normalizedSize) return;
      pendingManualVariantRef.current = normalizedSize;
      setMarketplaceSelectedSize(normalizedSize);
      const currentQuerySize = String(variantIdFromQuery || "")
        .trim()
        .toUpperCase();
      if (currentQuerySize !== normalizedSize) updateCollectionVariantQueryParam(normalizedSize);
      else pendingManualVariantRef.current = "";
      return;
    }
    const normalizedVariantId = Number(variantId);
    const normalizedSize = String(size || "");
    if (!Number.isFinite(normalizedVariantId) || normalizedVariantId <= 0) return;
    pendingManualVariantRef.current = String(normalizedVariantId);
    setSelectedVariantState((current) => {
      if (Number(current.id) === normalizedVariantId && current.size === normalizedSize) return current;
      return { id: normalizedVariantId, size: normalizedSize};
    });
    if (String(variantIdFromQuery || "") !== String(normalizedVariantId)) updateCollectionVariantQueryParam(normalizedVariantId);
    else pendingManualVariantRef.current = "";
  },[isCollectionProduct, variantIdFromQuery, updateCollectionVariantQueryParam]);

  if (isPageInvalid) {
    return (
      <div className="min-h-screen bg-white font-['Inter','Poppins',sans-serif] text-[#0f172a]">
        <NewNavbar showAmirCollection={isExclusiveProduct} logoRedirectPath={navbarLogoRedirectPath} />
        <main className="mx-auto max-w-[1320px] px-5 pb-[130px] py-12">
          <p className="text-sm text-[#80808C]">Invalid product.</p>
        </main>
        <Footer />
      {!isExclusiveProduct && (
        <FloatingChatBar
          onStartSurvey={handleChatBarStartSurvey}
          onRequireProfile={openSurveyGate}
          canUseRealChat={canUseRealChat}
          shouldShowSurveyPrompt={shouldShowSurveyPrompt}
          isProfileChecking={isProfileChecking}
          isAuthenticated={isAuthenticated}
        />
      )}
      </div>
    );
  }

  const productDetailsStatus = Number(
    productDetailsError?.status ??
      productDetailsError?.originalStatus ??
      productDetailsError?.data?.status,
  );
  const isProductNotFound = hasProductLookup && isDetailsError && productDetailsStatus === 404;

  if (isProductNotFound) {
    return (
      <div className="flex min-h-screen flex-col bg-white font-['Inter','Poppins',sans-serif] text-[#0f172a]">
        <Helmet>
          <title>Product Not Found | Eveness</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <NewNavbar showAmirCollection={isExclusiveProduct} logoRedirectPath={navbarLogoRedirectPath} />
        <main className="flex flex-1 items-center justify-center px-5 py-16">
          <div className="mx-auto w-full max-w-[520px] text-center">
            <div className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#F4F4F4]">
              <ItemNotFoundIcon/>
            </div>
            <h1 className="mt-6 text-[28px] font-semibold text-[#0F0F0F] sm:text-[34px]">
              Product not found
            </h1>
            <p className="mx-auto mt-3 max-w-[420px] text-sm leading-6 text-[#80808C] sm:text-base">
              This product may have been removed, is no longer available, or the
              product link may be incorrect.
            </p>
            <button
              type="button"
              onClick={() => navigate("/marketplace", { replace: true })}
              className="mt-7 inline-flex h-11 items-center justify-center rounded-[6px] bg-[#252525] px-6 text-sm font-medium text-white transition hover:bg-[#3A3A3A] focus:outline-none focus:ring-2 focus:ring-[#252525] focus:ring-offset-2"
            >
              Back to marketplace
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const shouldShowProductDetailsError =
    hasProductLookup &&
    isDetailsError &&
    !isProductDetailsLoading &&
    !isProductNotFound &&
    !product;
  const sidebarProductCount = isSurveySectionVisible ? 3 : 2;
  const sidebarRelatedProducts = displayRelatedProducts.slice(0,sidebarProductCount);
  const bottomRelatedProducts = displayRelatedProducts.slice(sidebarProductCount);
  const mobileRelatedProducts = displayRelatedProducts;
  const handleOpenTryOnModal = () => {
    if (!isAuthenticated) {
      toast.error("Please sign in to use this feature.");
      return;
    }

    if (
      uploadedTryOnPreview &&
      uploadedTryOnPreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(uploadedTryOnPreview);
    }
    setUploadedTryOnFile(null);
    setUploadedTryOnPreview("");
    setSelectedTryOnSource("");
    resetMultiTryOnStream();
    completedMultiTryOnRequestRef.current = "";
    setIsTryOnModalOpen(true);
  };

  const closeTryOnModal = () => {
    if (isUploadingTryOnImage || isMultiTryOnBusy)  return;

    if (uploadedTryOnPreview && uploadedTryOnPreview.startsWith("blob:")) {
      URL.revokeObjectURL(uploadedTryOnPreview);
    }

    setIsTryOnModalOpen(false);
    setUploadedTryOnFile(null);
    setUploadedTryOnPreview("");
    setSelectedTryOnSource("");
  };

  const handleTryOnFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PNG or JPG image.");
      event.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image size must be up to 10MB.");
      event.target.value = "";
      return;
    }

    if (uploadedTryOnPreview && uploadedTryOnPreview.startsWith("blob:")) {
      URL.revokeObjectURL(uploadedTryOnPreview);
    }

    const nextPreview = URL.createObjectURL(file);

    setUploadedTryOnFile(file);
    setUploadedTryOnPreview(nextPreview);
    setSelectedTryOnSource("upload");
  };

  const resolveSurveyPersonUrl = async () => {
    const response = userFullBodyImageData?.survey_full_body_url
      ? userFullBodyImageData
      : await refetchSurveyImage().unwrap();
    const personUrl = String(response?.survey_full_body_url || "").trim();
    if (!personUrl) {
      throw new Error("No survey full-body image found. Please complete your survey or upload another image.");
    }
    return personUrl;
  };

  const startTryOnStream = async ({
    resolvePersonUrl,
    pins = [],
  }) => {
      if (!isAuthenticated) {
        toast.error("Please sign in to use virtual try-on.");
        openLoginModal();
        return false;
      }
      const baseProductSlug = String(safeProduct?.real_slug || safeProduct?.slug || productSlug || "").trim();

      if (!baseProductSlug) {
        toast.error("The base product slug is not available.");
        return false;
      }

      if (!selectedSize) {
        toast.error("Please select a size first.");
        return false;
      }

      setIsPreparingMultiTryOn(true);
      try {
        const personUrl = String((await resolvePersonUrl?.()) || "").trim();

        if (!personUrl) {
          throw new Error("The selected person image is not available.");
        }

        const requestId = `${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 10)}`;

        resetMultiTryOnStream();
        completedMultiTryOnRequestRef.current = "";

        await triggerMultiGarmentTryOn(
          {
            person_url: personUrl,
            selected_size: selectedSize,
            product_slug: baseProductSlug,
            product_type: safeProduct?.productType || productType,
            pins,
            request_id: requestId,
          },
          false,
        ).unwrap();

        return true;
      } catch (error) {
        const errorMessage =
          error?.data?.detail ||
            error?.data?.message ||
            error?.message ||
            "Failed to start virtual try-on.";
        toast.error(
          typeof errorMessage === "string"
            ? errorMessage
            : "Failed to start virtual try-on.",
        );
        return false;
      } finally {
        setIsPreparingMultiTryOn(false);
      }
    };

  const handleOutfitReady = async ({ products }) => {
    const selectedProducts = Array.isArray(products) ? products : [];

    const pinSlugs = selectedProducts
      .map((item) =>
        String(
          item?.real_slug ||
            item?.slug ||
            item?.product_slug ||
            "",
        ).trim(),
      )
      .filter(Boolean);

    if (
      selectedProducts.length === 0 ||
      pinSlugs.length !== selectedProducts.length
    ) {
      toast.error(
        "One or more selected products do not have a valid slug.",
      );
      return false;
    }

    return startTryOnStream({
      resolvePersonUrl: resolveSurveyPersonUrl,
      pins: pinSlugs,
    });
  };

  const handleTryOnContinue = async () => {
    if (selectedTryOnSource === "survey") {
      return startTryOnStream({
        resolvePersonUrl: resolveSurveyPersonUrl,
        pins: [],
      });
    }

    if (selectedTryOnSource === "upload") {
      if (!uploadedTryOnFile) {
        toast.error("Please choose a photo first.");
        return false;
      }

      return startTryOnStream({
        resolvePersonUrl: () =>
          uploadToS3(uploadedTryOnFile, postUploadImage),
        pins: [],
      });
    }
    toast.error("Please select a survey image or upload a photo.");
    return false;
  };

  const renderSizeSelector = () => (
  <div className="mt-[10px]">
    <div className="mb-[10px] flex min-h-[20px] items-center gap-2">
      <p className="text-[14px] font-medium text-[#0F0F0F] font-helvena">
        Select Size
      </p>
      {isRecommendedSizeLoading ? (
        <span
          className="h-3 w-3 animate-spin rounded-full border-2 border-[#D7CBC7] border-t-[#8F333B]"
          aria-label="Loading recommended size"
        />
      ) : normalizedRecommendedSize ? (
        <span className="font-spaceMono text-[10px] uppercase tracking-[0.08em] text-[#8F333B] mt-1">
          Recommended: {normalizedRecommendedSize}
        </span>
      ) : null}
    </div>
    <div className="flex flex-wrap items-center gap-2">
      {displaySizeOptions.length > 0 ? (
        displaySizeOptions.map((option) => {
          const size = isCollectionProduct ? option.size : option;
          const variantId = isCollectionProduct ? option.id : null;
          const isOutOfStock = isCollectionProduct && option?.isOutOfStock === true;
          const isSelected = isCollectionProduct
            ? Number(selectedVariantId) === Number(variantId)
            : size === selectedSize;
          const isRecommended =
            Boolean(normalizedRecommendedSize) &&
            String(size || "").trim().toUpperCase() === normalizedRecommendedSize;
          return (
            <button
              key={isCollectionProduct ? variantId : size}
              type="button"
              disabled={isOutOfStock}
              aria-pressed={isSelected}
              onClick={() =>
                handleSizeSelection({
                  size,
                  variantId,
                })
              }
              className={[
                "flex touch-manipulation select-none items-center justify-center",
                "rounded-[6px] border font-spaceMono",
                "transition-colors duration-100 ease-out",
                "[-webkit-tap-highlight-color:transparent]",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#252525]/30",
                isRecommended
                  ? "h-[50px] min-w-[78px] px-3"
                  : "h-[42px] min-w-[47px] px-4",
                isSelected
                  ? "border-[#252525] bg-[#252525] text-white"
                  : "border-[#D9D5D0] bg-white text-[#3D3A37]",
                isOutOfStock
                  ? "cursor-not-allowed opacity-45"
                  : "md:hover:border-[#252525]",
              ].join(" ")}
            >
                <div className="flex flex-col items-center justify-center">
                  <span className="text-[12px] leading-none">
                    {size}
                  </span>
                  {isRecommended && (
                    <span
                      className={[
                        "mt-[5px] text-[8px] leading-none",
                        isSelected ? "text-white/80" : "text-[#8F333B]",
                      ].join(" ")}
                    >
                      Recommended
                    </span>
                  )}
                </div>
            </button>
          );
        })
      ) : (
        <p className="text-[13px] text-[#8A8580]">No sizes available.</p>
      )}
    </div>
  </div>
);

  return (
    <div className="min-h-screen bg-white font-['Inter','Poppins',sans-serif] text-[#0f172a]">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="title" content={seoTitle} />
        <meta name="description" content={seoDescription} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        {seoProduct.image ? (
          <meta property="og:image" content={seoProduct.image} />
        ) : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {seoProduct.image ? (
          <meta name="twitter:image" content={seoProduct.image} />
        ) : null}
      </Helmet>
      <NewNavbar showAmirCollection={isExclusiveProduct} logoRedirectPath={navbarLogoRedirectPath} />
      <HomeProfilePopupFlow
        isOpen={isHomeProfileFlowOpen}
        mode={isAuthenticated ? "restart" : "public"}
        initialStep={homeProfileInitialStep}
        initialProfile={
          homeProfileInitialStep >= 3
            ? detailSurveyProfile
            : null
        }
        autoAnalyzeOnOpen={
          homeProfileInitialStep === 3 &&
          !isFitScoreAccountFlow &&
          Boolean(detailSurveyProfile)
        }
        saveStepTitle={
          isFitScoreAccountFlow
            ? "Save your Account"
            : undefined
        }
        onClose={handleCloseHomeProfileFlow}
        onSignIn={isAuthenticated ? undefined : handleHomeProfileSignIn}
        onSignUp={isAuthenticated ? undefined : handleHomeProfileSignUp}
        isGoogleRedirecting={autoStartGoogle}
        onRestartComplete={handleHomeProfileComplete}
        isAuthenticated={isAuthenticated}
      />
      <AuthModal
        isOpen={authModalOpen}
        initialMode={authModalMode}
        autoStartGoogle={autoStartGoogle}
        preloadGoogle={isHomeProfileFlowOpen && !isAuthenticated}
        onGoogleStartConsumed={() => setAutoStartGoogle(false)}
        onClose={() => {
          setAuthModalOpen(false);
          setAutoStartGoogle(false);
          if (!isAuthenticated) {
            activateGuestMatchForCurrentProduct();
          }
        }}
      />

      <RootPersonalizationModal
        isOpen={rootPersonalizationOpen && shouldShowSurveyPrompt}
        variant={isAuthenticated ? "survey" : "guest"}
        onClose={() => setRootPersonalizationOpen(false)}
        onSignIn={() => {
          setRootPersonalizationOpen(false);
          openLoginModal();
        }}
        onUnlockMatch={startSurveyFlow}
      />
      <SaveToBoardModal
        isOpen={isSaveBoardModalOpen}
        product={saveBoardProduct}
        onClose={() => {
          setIsSaveBoardModalOpen(false);
          setSaveBoardProduct(null);
        }}
        onRequireAuth={() => {
          setIsSaveBoardModalOpen(false);
          setSaveBoardProduct(null);
          openLoginModal();
        }}
        onSaved={() => {
          setSaveBoardProduct(null);
        }}
      />

      <main className="mx-auto max-w-[1480px] px-4 pb-[130px] pt-4 md:px-5">
        <section className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_240px] 2xl:grid-cols-[minmax(0,1fr)_280px]">
          <div className="min-w-0 grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,565px)]">
            <div>
              <div className="relative grid h-[592px] overflow-hidden rounded-md bg-[#f4f5f7] lg:h-[650px] xl:h-[700px]">
                {isProductDetailsLoading ? (
                  <div className="grid h-full place-items-center">
                    <div className="flex items-center gap-2 text-sm text-[#80808C]">
                      <span>Loading product details...</span>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#D5D7DE] border-t-[#80808C]" />
                    </div>
                  </div>
                ) : shouldShowProductDetailsError ? (
                  <div>
                    <p className="text-[20px] font-semibold text-red-500">
                      Failed to load product details.
                    </p>
                  </div>
                ) : selectedProductImage && !selectedProductImageFailed ? (
                  <img
                    src={selectedProductImage}
                    alt={safeProduct.name || "Product"}
                    onError={() => handleProductImageError(selectedProductImage)}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-[#5F5F5D]">
                    <span className="font-inter text-[14px] font-normal text-white/35">
                      Product Image
                    </span>
                  </div>
                )}

                {!isExclusiveProduct && !isProductDetailsLoading && !shouldShowProductDetailsError ? (
                  <button
                    type="button"
                    onClick={handleOpenTryOnModal}
                    disabled={
                      !originalSelectedProductImage || isMultiTryOnBusy
                    }
                    className="h-[26px] absolute bottom-4 right-4 rounded-[5px] bg-white px-3 py-1 font-spaceMono text-[12px] text-[#434343] font-normal shadow-[0_1px_2px_rgba(0,0,0,0.12)] transition hover:bg-[#F4F4F4] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isMultiTryOnBusy ? "Trying On..." : "Virtual Try-On"}
                  </button>
                ) : null}

                {!isExclusiveProduct && isTryOnModalOpen ? (
                  <VirtualTryOnFlowModal
                    focalProduct={{
                      id: safeProduct?.id,
                      name: safeProduct?.name,
                      brand: safeProduct?.brand,
                      category: safeProduct?.garmentType,
                      size: selectedSize,
                      price:
                        selectedProductPricing?.discountedPrice ??
                        safeProduct?.discountedPrice ??
                        safeProduct?.price,
                      image:
                        originalSelectedProductImage ||
                        safeProduct?.image,
                      slug:
                        safeProduct?.real_slug ||
                        safeProduct?.slug ||
                        productSlug,
                      productType:
                        safeProduct?.productType || productType,
                    }}
                    isAuthenticated={isAuthenticated}
                    surveyImageUrl={
                      userFullBodyImageData?.survey_full_body_url || ""
                    }
                    isSurveyImageFetching={isSurveyImageFetching}
                    uploadedPreview={uploadedTryOnPreview}
                    selectedPhotoSource={selectedTryOnSource}
                    onSelectPhotoSource={setSelectedTryOnSource}
                    onFileChange={handleTryOnFileChange}
                    onExpressContinue={handleTryOnContinue}
                    onOutfitReady={handleOutfitReady}
                    onClose={closeTryOnModal}
                    isUploading={isUploadingTryOnImage}
                    isMultiTryOnGenerating={isMultiTryOnBusy}
                    multiTryOnStream={multiTryOnStream}
                  />
                ) : null}
              </div>
              <div className="mt-3 flex gap-3">
                {productImages.length > 0
                  ? productImages.map((image, idx) => (
                      <button
                        key={`${image}-${idx}`}
                        type="button"
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`grid h-[5.5rem] w-14 place-items-center rounded-md border text-xs overflow-hidden ${
                          idx === selectedImageIndex
                            ? "border-[#0f172a] text-gray-500"
                            : "border-[#edf0f3] bg-[#f5f6f8] text-gray-300"
                        }`}
                      >
                      {failedProductImages.has(image) ? (
                        <div className="flex h-full w-full items-center justify-center bg-[#5F5F5D]">
                          <span className="font-inter text-[8px] font-normal text-white/35">
                            Product Image
                          </span>
                        </div>
                      ) : (
                        <img
                          src={image}
                          alt={`${safeProduct.name || "Product"} thumbnail ${idx + 1}`}
                          onError={() => handleProductImageError(image)}
                          className="h-full w-full rounded-md object-cover"
                        />
                      )}
                      </button>
                    ))
                  : [1, 2, 3, 4].map((thumb) => (
                      <button
                        key={thumb}
                        type="button"
                        className="grid h-[5.5rem] w-14 place-items-center rounded-md border border-[#edf0f3] bg-[#f5f6f8] text-xs text-gray-300"
                      >
                        {thumb}
                      </button>
                    ))}
              </div>{" "}
            </div>

            <div className="min-w-0 w-full font-inter lg:max-w-[560px]">
              {isProductDetailsLoading ? (
                <div className="space-y-2">
                  <div className="h-7 w-3/4 animate-pulse rounded bg-[#EEF1F4]" />
                  <div className="h-5 w-24 animate-pulse rounded bg-[#EEF1F4]" />
                </div>
              ) : isDetailsError ? (
                <div>
                  <p className="text-[20px] font-semibold text-red-500">
                    Failed to load product details.
                  </p>
                </div>
              ) : (
                <>
                  <p className="mt-1 text-xs text-[#8A8580] font-normal font-spaceMono">
                    {safeProduct?.brand || "Brand"}
                  </p>

                  <p className="text-[28px] font-semibold font-helvena text-[#0F0F0F] mt-2 sm:mt-1">
                    {safeProduct.name || "Product"}
                  </p>

                  <div className="mt-2 flex min-h-[32px] flex-wrap items-center gap-2 sm:mt-1">
                    {selectedProductPricing.hasDiscount ? (
                      <span className="text-[16px] font-medium tabular-nums text-[#8A8A8A] line-through">
                        {formatStorePrice(
                          selectedProductPricing.originalPrice,
                        )}
                      </span>
                    ) : null}

                    <span
                      className={[
                        "inline-block min-w-[72px] text-[22px] font-semibold tabular-nums",
                        "transition-colors duration-100",
                        selectedProductPricing.hasDiscount
                          ? "text-[#257A45]"
                          : "text-[#0F0F0F]",
                      ].join(" ")}
                    >
                      {selectedProductPricing.originalPrice > 0
                    ? formatStorePrice(
                        selectedProductPricing.discountedPrice,
                      )
                    : "N/A"}
                    </span>

                    {selectedProductPricing.hasDiscount ? (
                      <span className="rounded-[4px] bg-[#EAF7EE] px-2 py-1 font-spaceMono text-[10px] font-semibold text-[#257A45]">
                        {Number(
                          selectedProductPricing.discountPercent.toFixed(2),
                        )}
                        % OFF
                      </span>
                    ) : null}
                  </div>
                  {!isProductDetailsLoading && !isDetailsError ? renderSizeSelector() : null}
                </>
              )}
              <div className="mt-3">
                {isCollectionProduct ? (
                  /*
                  * Collection product
                  */
                  <>
                  <div className={[
                  "grid gap-[5px]",
                  isExclusiveProduct
                    ? "grid-cols-[minmax(0,1fr)_120px]"
                    : "grid-cols-[minmax(0,1fr)_50px_120px] md:grid-cols-[minmax(0,1fr)_90px_120px]"
                    ].join(" ")}>
                  {isCollectionProductOutOfStock ? (
                    <button
                      type="button"
                      disabled
                      className="
                        flex h-[40px] cursor-not-allowed items-center justify-center
                        rounded-[5px] bg-[#B8B8B8] px-4
                        text-center text-[14px] font-normal text-white opacity-80
                        sm:h-[46px]
                      "
                    >
                      Out of Stock
                    </button>
                  ) : !isAdded ? (
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      disabled={isAddToCartDisabled}
                      className={[
                        "flex h-[40px] items-center justify-center rounded-[5px] px-4",
                        "text-center font-helvena text-[14px] font-normal text-white",
                        "transition-opacity sm:h-[46px]",
                        isAddToCartDisabled
                          ? "cursor-not-allowed bg-[#B8B8B8] opacity-70"
                          : "bg-[#252525] hover:opacity-90",
                      ].join(" ")}
                    >
                      {!selectedCollectionVariant
                        ? "Select a Size"
                        :   <>
                          <span className="mr-[10px]">Add to Cart</span>
                          <CartIcon />
                        </>
                      }
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate("/cart")}
                      className="
                        flex h-[40px] items-center justify-center rounded-[5px]
                        bg-[#252525] px-4 text-center font-helvena
                        text-[14px] font-normal text-white
                        transition-opacity hover:opacity-90 sm:h-[46px]
                      "
                    >
                      <span className="mr-[10px]">Go to Cart</span>
                      <CartIcon />
                    </button>
                  )}
                {!isExclusiveProduct && (
                  <button
                    type="button"
                    onClick={() => handleSaveBoard(safeProduct)}
                    disabled={
                      !safeProduct?.id ||
                      isProductDetailsLoading
                    }
                    aria-label="Add product to board"
                    className="
                      flex h-[40px] items-center justify-center
                      rounded-[5px] border border-[#E6E6E6]
                      bg-white text-[#0F0F10] transition
                      hover:bg-[#F7F7F7]
                      disabled:cursor-not-allowed disabled:opacity-50
                      sm:h-[46px]
                    "
                  >
                    <ProductHeart />
                  </button>
                )}
                {/*
                  * Express checkout (Apple Pay / Google Pay). Renders itself
                  * only when the browser actually has a wallet, and skips both
                  * the cart and checkout — the wallet supplies the email and
                  * the address, so a guest needs no form.
                  */}
                <QuickPayButton
                  express
                  className="w-full min-w-0"
                  items={quickPayItems}
                  amount={quickPayAmount}
                  disabled={
                    isAddToCartDisabled || isCollectionProductOutOfStock
                  }
                  buttonHeight={quickPayButtonHeight}
                />
                </div>
                {isExclusiveProduct && (
                  <button
                    type="button"
                    onClick={() => openExclusiveBundleModal("cart")}
                    disabled={isBundleLoading}
                    className={[
                      "mt-[5px] flex h-[40px] w-full",
                      "items-center justify-center rounded-[5px]",
                      "border border-[#252525] bg-[#252525] px-4",
                      "font-helvena text-[14px] font-normal",
                      "text-white transition",
                      "sm:h-[46px]",
                      isBundleLoading
                        ? "cursor-wait opacity-60"
                        : "hover:opacity-[95%]",
                    ].join(" ")}
                  >
                  {isBundleLoading
                    ? "Loading Bundle..."
                    : exclusiveBundleDetails?.discountPercent
                      ? `Bundle the entire Collection for ${exclusiveBundleDetails.discountPercent}% Off.`
                      : "Bundle the entire Collection"}
                    </button>
                  )}
                  </>
                ) : isEvenessProduct ? (
                  isAdded ? (
                    <button
                      type="button"
                      onClick={() => navigate("/cart")}
                      className="
                        flex h-[52px] w-full items-center justify-center
                        rounded-[5px] bg-[#252525] px-4
                        text-center text-[14px] font-normal text-white
                        transition-opacity hover:opacity-90
                      "
                    >
                      <span className="mr-[10px]">Go to Cart</span>
                      <CartIcon />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      disabled={isAddToCartDisabled}
                      className={[
                        "flex h-[52px] w-full items-center justify-center rounded-[5px]",
                        "px-4 text-center text-[14px] font-normal text-white",
                        "transition-opacity",

                        isAddToCartDisabled
                          ? "cursor-not-allowed bg-[#B8B8B8] opacity-70"
                          : "bg-[#252525] hover:opacity-90",
                      ].join(" ")}
                    >
                    <span className="mr-[10px]">Add to Cart</span>
                      <CartIcon />
                    </button>
                  )
                ) : (
                  <div className="grid grid-cols-[minmax(0,1fr)_50px] gap-[5px] md:grid-cols-[minmax(0,1fr)_73px]">
                    <a
                      href={safeProduct.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => {
                        if (!safeProduct.url) {
                          event.preventDefault();
                          toast.error("Product URL is not available.");
                        }
                      }}
                      className="
                        flex h-[40px] w-full items-center justify-center
                        rounded-[5px] bg-[#252525] px-4
                        text-center font-helvena text-[14px] font-normal
                        text-white transition-opacity hover:opacity-90
                        sm:h-[46px]
                      "
                    >
                      buy at {productWebsite}
                    </a>
                    <button
                      type="button"
                      onClick={() => handleSaveBoard(safeProduct)}
                      aria-label="Add product to board"
                      className="
                        flex h-[40px] items-center justify-center
                        rounded-[5px] border border-[#E6E6E6]
                        bg-white text-[#0F0F10]
                        transition hover:bg-[#F7F7F7]
                        sm:h-[46px]
                      "
                    >
                      <ProductHeart />
                    </button>
                  </div>
                )}           
              </div>

          {!isExclusiveProduct && (
            canShowMatchAnalysis ? (
            <div className="mt-3 rounded-[5px] border border-[#D8D5D1] bg-[#F4F2EE] px-4 py-4 sm:px-5">
              <div className="mb-3 flex items-center gap-2">
                <span className="font-spaceMono text-[10px] tracking-[0.16em] text-[#7E2340]">
                  {"// Your Match Score:"}
                </span>

                {isMatchSectionLoading && (
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-[#D7CBC7] border-t-[#8F333B]" />
                )}
              </div>

              {/* Score and GPT buttons */}
              <div className="grid grid-cols-2 gap-2.5">
                <div
                  className="
                    flex h-[30px] min-w-0 items-center justify-center
                    rounded-[5px] bg-[#8F333B] px-3
                    text-center font-spaceMono text-[10px] font-bold
                    tracking-[0.08em] text-white
                    sm:h-[34px] sm:text-[11px]
                  "
                  aria-live="polite"
                >
                  {matchData.matchPercentage !== null &&
                  matchData.matchPercentage !== undefined
                    ? `${matchData.matchPercentage}% match score`
                    : isMatchPercentageLoading
                      ? "Analyzing..."
                      : "?% match score"}
                </div>
                <button
                  type="button"
                  onClick={handleAskEvenessGpt}
                  disabled={
                    isProductTransferLoading ||
                    isGlobalChatStreaming ||
                    isMatchSectionLoading ||
                    !matchDescription
                  }
                  className="
                    flex h-[30px] min-w-0 items-center justify-center
                    gap-3 rounded-[5px] bg-[#313131] px-3
                    font-spaceMono text-[10px] font-normal
                    tracking-[0.12em] text-white
                    transition hover:bg-[#252525]
                    disabled:cursor-not-allowed
                    disabled:opacity-60
                    disabled:hover:bg-[#313131]
                    sm:h-[34px] sm:gap-5 sm:text-[11px]
                  "
                >
                  {isProductTransferLoading ? (
                    <>
                      <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      <span className="truncate">Starting GPT...</span>
                    </>
                  ) : (
                    <>
                      <span className="truncate">Ask Eveness GPT</span>
                      <AskEvenessIcon className="h-[14px] w-[18px] shrink-0 text-white sm:h-[16px] sm:w-[22px]" />
                    </>
                  )}
                </button>
              </div>
              {/* Streaming description */}
              <div
                className="
                  mt-2.5 rounded-[5px] bg-white
                  px-4 py-4 sm:px-5 sm:py-5
                  text-[#818181] font-helvena font-normal
                  text-[12px] sm:text-[14px]
                "
                aria-live="polite"
                aria-busy={isMatchSummaryLoading}
              >
                {matchDescription ? (
                  <div>
                    <MarkdownResponse message={matchDescription} />
                  </div>
                ) : isMatchSummaryLoading ? (
                  <div className="space-y-2">
                    <div className="h-4 w-[110px] animate-pulse rounded bg-[#EAE7E3]" />
                    <div className="h-3 w-full animate-pulse rounded bg-[#EEECE9]" />
                    <div className="h-3 w-[92%] animate-pulse rounded bg-[#EEECE9]" />
                    <div className="h-3 w-[75%] animate-pulse rounded bg-[#EEECE9]" />
                  </div>
                ) : isUserMatchError ? (
                  <p className="font-helvena text-[13px] text-red-500">
                    Failed to load match analysis.
                  </p>
                ) : (
                  <p className="font-helvena text-[13px] text-[#898989]">
                    Match analysis is not available for this product yet.
                  </p>
                )}
                {isMatchSectionLoading && styleTags.length === 0 ? (
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <span className="font-spaceMono text-[10px] uppercase tracking-[0.08em] text-[#77736F]">
                    Style Tags
                  </span>

                    <span className="animate-pulse rounded-full bg-[#F0EDE9] px-3 py-1 font-helvena text-[10px] text-[#8A8580]">
                      Loading...
                    </span>
                  </div>
                  ) : styleTags.length > 0 ? (
                  <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-[#EEEAE6] pt-4">
                    <span className="mr-1 font-spaceMono text-[10px] uppercase tracking-[0.08em] text-[#77736F]">
                      Style Tags
                    </span>
                    {styleTags.map((tag, index) => (
                      <span
                        key={`${String(tag)}-${index}`}
                        className="
                          rounded-full border border-[#DDD8D3]
                          bg-[#F4F2EE] px-3 py-1
                          font-helvena text-[10px] text-[#5C5C60]
                        "
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
                </div>
              ) : hasPendingSurveyToken ? (
                  <div className="flex flex-col bg-[#F4F2EE] rounded-[5px] px-[20px] mt-3">
                    <div className="my-[10px]">
                      <span className="text-[#7E2340] font-spaceMono text-[11px]">
                        {"// Your Match Score:"}
                      </span>
                    </div>
                    <div className="flex flex-row text-white font-spaceMono font-bold text-[11px] gap-[10px] w-full">
                      <button className="bg-[#8F333B] h-[34px] rounded-[5px] w-full opacity-[50%]" disabled>?% match score</button>
                      <button className="bg-[#313131] h-[34px] rounded-[5px] w-full flex flex-row justify-center items-center gap-[10px] md:gap-[20px] opacity-[50%]" disabled><div>Ask Eveness GPT</div><AskEvenessIcon /></button>
                    </div>
                    <div className="mt-[10px] rounded-[5px] bg-white p-4 shadow-sm mb-[10px]">
                      <div className="mb-3 bg-[#F4F2EE] rounded-full h-[32px] w-[32px] flex justify-center items-center">
                        <SurveyLockedIcon />
                      </div>
                      <h3 className="font-spaceMono text-[14px] font-bold text-[#7E2340]">
                        Sign in for your fit score
                      </h3>
                      <p className="mt-[6px] font-spaceMono text-[12px] leading-[150%] text-[#7E2340]">
                        Get a personalized match score based on your measurements and style
                        preferences.
                      </p>
                      <button
                        type="button"
                        onClick={openFitScoreAccountFlow}
                        className="mt-3 h-[42px] rounded-full bg-[#8F333B] hover:opacity-[95%] px-6 font-spaceMono text-[12px] font-bold text-white shadow-[0px_4px_12px_-2px_#0000001A]"
                      >
                        Sign in or create account →
                      </button>
                    </div>
                  </div>
              ) : isSurveySectionVisible ? (
                <ProfileMeasurementsForm
                  variant="details"
                  initialProfile={detailSurveyProfile}
                  showSignInText={false}
                  containerClassName="border border-[#E7E0DB]"
                  onSignUpLogin={openLoginModal}
                  onAnalyze={(profile) => {
                    setDetailSurveyProfile(profile);
                    setIsFitScoreAccountFlow(false);
                    setHomeProfileInitialStep(3);
                    setRootPersonalizationOpen(false);
                    setAuthModalOpen(false);
                    setIsHomeProfileFlowOpen(true);
                  }}
                  hideAuthActions={isAuthenticated && profileMissing}
                />
              ) : null)}

              <div className="mt-5">
                <h2 className="text-xl font-semibold">Product Details</h2>

                <div className="mt-2 overflow-hidden rounded-md border border-[#edf0f3]">
                  {detailRows.map((row, idx) => {
                    const isOpen = openDetailSections[row.title];

                    return (
                      <div
                        key={row.title}
                        className={
                          idx !== detailRows.length - 1
                            ? "border-b border-[#edf0f3]"
                            : ""
                        }
                      >
                        <button
                          type="button"
                          onClick={() => toggleDetailSection(row.title)}
                          className="flex w-full items-center justify-between px-3 py-3 text-left text-sm"
                        >
                          <span className="font-medium text-[#374151]">
                            {row.title}
                          </span>
                          <span className="text-[16px] text-gray-500">
                            {isOpen ? "⌃" : "⌄"}
                          </span>
                        </button>

                        {isOpen && (
                          <div className="px-3 pb-3 text-sm">
                            {row.link ? (
                              <a
                                href={row.link}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[#2563eb] hover:underline"
                              >
                                {row.content}
                              </a>
                            ) : row.isHtml ? (
                              <div
                                className="prose prose-sm max-w-none text-gray-500"
                                dangerouslySetInnerHTML={{
                                  __html: row.content,
                                }}
                              />
                            ) : (
                              <p className="text-gray-500">{row.content}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <aside className="hidden min-w-0 xl:block">
            {shouldHideRecommendationCards ? (
              <div className="grid grid-cols-1 gap-3">
                {Array.from({ length: sidebarProductCount }).map((_, index) => (
                  <div
                    key={`sidebar-loader-${index}`}
                    className="h-[320px] animate-pulse rounded-[8px] bg-[#EEF1F4]"
                  />
                ))}
              </div>
            ) : sidebarRelatedProducts.length > 0 ? (
              <div className="grid grid-cols-1 gap-3">
                {sidebarRelatedProducts.map((item, idx) => (
                  <div key={`sidebar-related-${item.id}-${idx}`} className="min-w-0">
                    <ProductCard
                      key={`details-column-${item.id}-${idx}`}
                      item={item}
                      imageHeightClass={[
                        "h-[240px] sm:h-[340px]",
                        "h-[260px] sm:h-[370px]",
                      ]}
                      matchPercentage={item.matchPercentage}
                      onSaveBoard={handleSaveBoard}
                      isSavingBoard={false}
                      compactActions
                      hideActions={isExclusiveProduct}
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </aside>{" "}
        </section>
        <section className="font-inter">
          {shouldHideRecommendationCards ? (
            <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-5">
              {Array.from({ length: 10 }).map((_, index) => (
                <div
                  key={index}
                  className="h-[320px] animate-pulse rounded-[8px] bg-[#EEF1F4]"
                />
              ))}
            </div>
          ) : isTopProductsError ? (
            <p className="mt-3 text-sm text-red-500">
              Failed to load product recommendations.
            </p>
          ) : displayRelatedProducts.length === 0 ? (
            <p className="mt-3 text-sm text-[#80808C]">
              No related products found.
            </p>
          ) : (
            <>
              {isCollectionProduct ? (
                <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 xl:hidden">
                  {mobileRelatedProducts.map((item, idx) => (
                    <div
                      key={`mobile-related-${item.id}-${idx}`}
                      className="min-w-0"
                    >
                      <ProductCard
                        item={item}
                        imageHeightClass={getProductImageHeightClass(item, idx)}
                        matchPercentage={item.matchPercentage}
                        onSaveBoard={handleSaveBoard}
                        isSavingBoard={false}
                        hideActions={isExclusiveProduct}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <StableMasonryGrid
                  items={mobileRelatedProducts}
                  className="mt-3 xl:hidden"
                  keyPrefix="mobile-related"
                  mobileColumns={2}
                  tabletColumns={3}
                  desktopColumns={3}
                  wideColumns={3}
                  renderItem={(item, idx) => (
                    <ProductCard
                      item={item}
                      imageHeightClass={getProductImageHeightClass(item, idx)}
                      matchPercentage={item.matchPercentage}
                      onSaveBoard={handleSaveBoard}
                      isSavingBoard={false}
                    />
                  )}
                />             
              )}

              {bottomRelatedProducts.length > 0 ? (
                isCollectionProduct ? (
                  <div className="mt-3 hidden xl:grid xl:grid-cols-4 xl:gap-3 2xl:grid-cols-5">
                    {bottomRelatedProducts.map((item, idx) => {
                      const originalIndex = idx + sidebarRelatedProducts.length;

                      return (
                        <div
                          key={`bottom-related-${item.id}-${idx}`}
                          className="min-w-0"
                        >
                          <ProductCard
                            item={item}
                            imageHeightClass={getProductImageHeightClass(
                              item,
                              originalIndex,
                            )}
                            matchPercentage={item.matchPercentage}
                            onSaveBoard={handleSaveBoard}
                            isSavingBoard={false}
                            hideActions={isExclusiveProduct}
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <StableMasonryGrid
                    items={bottomRelatedProducts}
                    className="mt-3 hidden xl:block"
                    keyPrefix="bottom-related"
                    mobileColumns={4}
                    tabletColumns={4}
                    desktopColumns={4}
                    wideColumns={5}
                    renderItem={(item, idx) => {
                        const originalIndex = idx + sidebarRelatedProducts.length;
                        return (
                            <ProductCard
                              item={item}
                              imageHeightClass={getProductImageHeightClass(
                                item,
                                originalIndex,
                              )}
                              matchPercentage={item.matchPercentage}
                              onSaveBoard={handleSaveBoard}
                              isSavingBoard={false}
                            />
                        );
                      }}
                  />
                )
              ) : null}
            </>
          )}
        {isMarketplaceRecommendations &&
        !shouldHideRecommendationCards &&
        !isTopProductsError &&
        displayRelatedProducts.length > 0 &&
        (topProductsHasMore || isLoadingMoreTopProducts) ? (
          <div
            ref={topProductsLoadMoreRef}
            className="flex min-h-[80px] items-center justify-center"
            aria-live="polite"
          >
            {isLoadingMoreTopProducts ? (
              <div className="flex items-center gap-2 text-sm text-[#80808C]">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#D8DADF] border-t-[#111111]" />
                Loading more products...
              </div>
            ) : null}
          </div>
        ) : null}
        </section>
      </main>
      <Footer />
      <AddedToCartDrawer
        isOpen={isCartDrawerOpen}
        productId={cartDrawerProductId}
        bundleId={cartDrawerBundleId}
        bundleInstanceId={cartDrawerBundleInstanceId}
        onClose={() => {
          setIsCartDrawerOpen(false);
        }}
        onCheckout={({
          productId,
          bundleId,
          bundleInstanceId,
        }) => {
          const isAuthenticated = Boolean(localStorage.getItem("authToken"));
          const isBundleCheckout = Boolean(bundleInstanceId || bundleId);
          const checkoutState = isBundleCheckout
            ? {
                checkoutMode: "bundle",
                checkoutBundleId: bundleId,
                checkoutBundleInstanceId:
                  bundleInstanceId,
              }
            : {
                checkoutMode: "single",
                checkoutProductId: productId,
              };
          if (isAuthenticated) navigate("/shipping-address", { state: checkoutState });
          else navigate("/order-information", { state: checkoutState });
        }}
      />
    {!isExclusiveProduct && (
      <FloatingChatBar
        onStartSurvey={handleChatBarStartSurvey}
        onRequireProfile={openSurveyGate}
        canUseRealChat={canUseRealChat}
        shouldShowSurveyPrompt={shouldShowSurveyPrompt}
        isProfileChecking={isProfileChecking}
        isAuthenticated={isAuthenticated}
      />
    )}
    {isExclusiveProduct && (
      <ExclusiveBundleSizingModal
        isOpen={isExclusiveBundleSizingModalOpen}
        products={exclusiveBundleProducts}
        bundle={exclusiveBundleDetails}
        mode={exclusiveBundleSizingModalMode}
        onClose={closeExclusiveBundleModal}
        onConfirm={confirmExclusiveBundle}
      />
    )}
    </div>
  );
}

export default FashionDetailsPage;
